import React, { useState, useEffect, useRef, useMemo } from "react";
import "../../styles/ChatBot.css";
import { useLocation, useHistory, useNavigate } from "react-router-dom";
import { createBrowserHistory } from "history";
import { useDispatch, useSelector } from "react-redux";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { default as ReactMarkdown } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import SyntaxHighlighter from "react-syntax-highlighter";
import {
  atomDark,
  dracula,
} from "react-syntax-highlighter/dist/cjs/styles/prism";
import { duotoneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
//import "github-markdown-css";
import Loader from "../../components/Loader";
import CustomToaster from "../../components/CustomToaster";
import { CiRepeat } from "react-icons/ci";
import StarRating from "../../components/StarRating";
import {
  Grid,
  Accordion,
  Popup,
  Card,
  Modal,
  Header,
  Form,
  Radio,
  Button,
  Icon,
} from "semantic-ui-react";
import { uploadFileToDataset, validateFile, formatFileSize, DocumentStatusPoller, checkDocumentTrainingStatus } from "../../services/fileUploadService";
import { createQuickChatDataset } from "../../services/quickChatService";

import {
  getChatHistoryConversation,
  resetChatHistoryConversation,
  qnaChatbotSession,
  QnaChatbotSuccess,
  createChatHistoryData,
  createChatHistoryDataSuccess,
  SaveHistoryConversation,
  getDocumentsDetails,
  getDocumentSuccess,
  getDocumantSummerization,
  DocumentSummarizationSuccess,
  feedbackServiceRequest,
  getDefaultFeedbackSuggestion,
  getChatLikesDislikeStatus,
  FeedbackServiceApiSuccess,
  fetchDocSettings,
  fetchDocSettingsSuccess,
  fetchDataSetParameters,
  getParametersSuccess,
  humanAgentRegister,
  humanAgentUpdateStatus,
  humanAgentGetStatus,
  humanAgentQna,
  getHumanAgentQnaChat,
  humanAgentValidate,
  getAgentEmail,
  resetUserQuestion,
  resetAgentResponse,
  resetAgentStatus,
  resetQuestion,
  resetAnswer,
  fetchAssistantParameters,
  fetchCombinedData,
  getDatasets,
  saveTokenUsageAction,
  deleteChatHistoryData,
  DeleteChatHistorySuccess,
} from "../../actions/dataConsoleAction";
import { Image, Input } from "semantic-ui-react";
import AiLoader from "../../components/AiLoader";
import ConfirmActionModalComponent from "../../uiComponents/ConfirmActionModal/ConfirmActionModalComponent";
import Summarization from "./Summarization";
import { fontSize } from "@mui/system";
import { set } from "react-hook-form";
import { notifyAgentEvent, clearAgentNotifications } from "../../utils/agentNotify";
import { injectAgentHelpLink, buildAgentHelpAnchorRenderer } from "../../utils/liveAgentHelp";
import { formatMessageTimestamp, formatMessageTimestampFull } from "../../utils/formatMessageTimestamp";
import { getChatLikesStatus, updateHumanInLoopRefs, getImageBlob, getFileContentBlob } from "../../apis";
import AuthenticatedImage, { BLANK_IMAGE_PLACEHOLDER } from "../../components/AuthenticatedImage";
import PDFViewer from "../Dataconsole/PDFViewer";
import { whitelist } from "validator";
import "react-image-lightbox/style.css"; // Import the lightbox CSS
import Lightbox from "react-image-lightbox";

// ----------------------------
// Polite waiting messages shown when bot takes longer than 15 seconds
// ----------------------------
const delayedWaitingMessages = [
  "Still working on your request — thank you for your patience!",
  "Hang tight! Our bot is carefully crafting the best response for you.",
  "Almost there — great answers take a little extra time!",
  "Our bot is doing some heavy thinking — please bear with us!",
  "Thank you for waiting — we're making sure you get the most helpful answer.",
  "Good things come to those who wait — your response is on its way!",
  "We appreciate your patience — the bot is working hard behind the scenes!",
  "Just a moment longer — we want to make sure we get this right for you.",
  "Your question deserves a thoughtful answer — we'll be right with you!",
  "The bot is digging deep for the best response — thanks for hanging in there!",
];

// ----------------------------
// New LoadingDots Component
// ----------------------------
const LoadingDots = () => {
  const [waitingMessage, setWaitingMessage] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * delayedWaitingMessages.length);
      setWaitingMessage(delayedWaitingMessages[randomIndex]);
    }, 12000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="loading-dots-container">
      <span className="loading-dots">
        <span>●</span>
        <span>●</span>
        <span>●</span>
      </span>
      {waitingMessage && (
        <div className="delayed-waiting-message">{waitingMessage}</div>
      )}
    </div>
  );
};

// ----------------------------
// New Typewriter Component
// ----------------------------
// This component gradually reveals the provided text.
const Typewriter = ({ text, speed = 5 }) => {
  // counter tracks how many characters to show
  const [counter, setCounter] = useState(0);

  // Reset counter when text changes
  useEffect(() => {
    setCounter(0);
  }, [text]);


  // Increase counter until the full text is revealed
  useEffect(() => {
    if (counter < text.length) {
      const timer = setTimeout(() => {
        setCounter(counter + 1);
      }, speed);
      return () => clearTimeout(timer);
    }
  }, [counter, text, speed]);

  return <span>{text.substring(0, counter)}</span>;
};

const mdComponents = {
  table: ({ children, ...props }) => (
    <div className="table-scroll-wrapper">
      <table {...props}>{children}</table>
    </div>
  ),
};

const TypewriterMarkdown = ({ text, speed = 10, skip = false, onComplete, components }) => {
  const [typedText, setTypedText] = useState("");
  const completedRef = useRef(false);

  useEffect(() => {
    if (skip || completedRef.current) {
      setTypedText(text);
      if (onComplete) onComplete();
      return;
    }
    setTypedText(""); // reset on new text
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setTypedText(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(interval);
        completedRef.current = true;
        if (onComplete) onComplete();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed, skip]);

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={components || mdComponents}>
      {typedText}
    </ReactMarkdown>
  );
};


const Message = ({
  text,
  isQuestion,
  sourceData,
  imageBlobPaths,
  imageBlobURLs,
  imageApiEndpoint,
  enabledownloadFlagLoader,
  disabledownloadFlagLoader,
  chatConversationId,
  suggestionOptions,
  userQuestion,
  chatHistoryIds,
  handleRegenerate,
  handleSendQuestion,
  isLastResponse, // New prop to indicate if this is the last response
  isRegenerate,
  isInitialGreeting,
  classification, // New prop
  showImagesOption,
  handleFeedbackOptionClick,
  quickResponses, // New prop for quick responses
  handleQuickResponseClick, // New prop for handling quick response clicks
  sendFeed,
  setSendFeed,
  isFromChatHistory, // New prop to indicate if the message is from chat history
  DocSettingsvalue, // Added DocSettingsvalue as a prop
  successAgentEmail, // Added successAgentEmail as a prop
  showSource, // Whether to show source cards
  onTypewriterComplete, // Callback when typewriter finishes
  isAgentHelp, // Whether live agent help is active
  isAgentHelpRequested, // Whether user has requested live agent help (modal submitted)
  isAgentScreen, // Whether this is the agent screen
  timestamp,
  isAgentMessage, // Whether this is part of a human-agent conversation (renders timestamp on either side)
}) => {

  const queryParams = new URLSearchParams(location.search);
  const currentUserType = useSelector((state) => state.auth.user);
  const [toasterVisible, setToasterVisible] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterAppearance, setToasterAppearance] = useState("success");
  const [thubmsUpModel, setThubmsUpModel] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [lengthError, setLengthError] = useState(true);
  const [specialChar, setSpecialChar] = useState(true);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [conversationId, setConversationId] = useState("");
  const [radioValues, setRadioValues] = useState(null);
  const [feedbackRequestFlag, setFeedbackRequestFlag] = useState(false);
  const [chatCount, setChatCount] = useState([]);
  const [suggestionTypes, setSuggestionTypes] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewDocUrl, setPreviewDocUrl] = useState("");
  const [PreviewDocFilename, setPreviewDocFilename] = useState("");
  // const [messages, setMessages] = useState([]);
  const greetingAddedRef = useRef(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentFilenames, setCurrentFilenames] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);
  const [showSatisfactionCard, setShowSatisfactionCard] = useState(true);
  const [showFeedbackOptions, setShowFeedbackOptions] = useState(false);
  const [showOtherFeedbackInput, setShowOtherFeedbackInput] = useState(false);
  const [input, setInput] = useState("");
  const [selectedFeedbackOption, setSelectedFeedbackOption] = useState("");
  const [latestFeedback, setLatestFeedback] = useState({ id: null, type: null });
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  const openViewer = (filenames = [], imgIndex = 0) => {
    if (filenames.length === 0) {
      console.error("No filenames provided!");
      return;
    }
    setCurrentFilenames(filenames);
    setCurrentImageIndex(imgIndex);
    setIsViewerOpen(true);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
  };

  // Effect to automatically trigger next image after lightbox is opened
  useEffect(() => {
    if (isViewerOpen && currentFilenames.length > 1) {
      const timer = setTimeout(() => {
        setCurrentImageIndex((currentImageIndex + 1) % currentFilenames.length);
      }, 50);

      return () => clearTimeout(timer); // Cleanup the timer on unmount or when viewer closes
    }
  }, [isViewerOpen]);

  // Lightbox needs plain src strings (mainSrc/nextSrc/prevSrc are required
  // props of the third-party Lightbox component), so it resolves its own
  // authenticated fetches independently of the thumbnail grid — a redundant
  // fetch for whichever image was already shown as a thumbnail, traded for
  // not having to share fetched-image state across components.
  const [lightboxSrcs, setLightboxSrcs] = useState({ main: null, next: null, prev: null });
  useEffect(() => {
    if (!isViewerOpen || currentFilenames.length === 0) {
      return undefined;
    }
    const paths = {
      main: currentFilenames[currentImageIndex]?.trim(),
      next: currentFilenames[(currentImageIndex + 1) % currentFilenames.length]?.trim(),
      prev: currentFilenames[(currentImageIndex + currentFilenames.length - 1) % currentFilenames.length]?.trim(),
    };
    let cancelled = false;
    const createdUrls = [];
    setLightboxSrcs({ main: null, next: null, prev: null });
    Object.entries(paths).forEach(([slot, path]) => {
      if (!path || !imageApiEndpoint) return;
      getImageBlob(path, imageApiEndpoint)
        .then((blob) => {
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          createdUrls.push(objectUrl);
          setLightboxSrcs((prev) => ({ ...prev, [slot]: objectUrl }));
        })
        .catch((error) => {
          console.error("Failed to load lightbox image", path, error);
        });
    });
    return () => {
      cancelled = true;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [isViewerOpen, currentImageIndex, currentFilenames, imageApiEndpoint]);

  const datasetId = queryParams.get("c");
  const chatID = queryParams.get("cid");
  const datasetTitle = queryParams.get("title");
  const dispatch = useDispatch();
  const { feedbackInitialData, feedbackServiceRequestStatus } = useSelector(
    (state) => state.chatFeedbackService
  );

  const { chatStatusData, chatLikeStatus } = useSelector(
    (state) => state.chatStatusLikeDislike || {}
  );

  const [activeIndex, setActiveIndex] = useState(1);

  const showToaster = (message, appearance) => {
    setToasterVisible(true);
    setToasterMessage(message);
    setToasterAppearance(appearance);
  };

  const closeToaster = () => {
    setToasterVisible(false);
  };

  const handleClick = (e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);
  };

  const filename =
    sourceData !== undefined && sourceData.length > 0
      ? sourceData[0].source.split("/").pop()
      : "";

  const pages =
    sourceData !== undefined && sourceData.length > 0 ? sourceData[0].page : "";

  useEffect(() => {
    if (feedbackServiceRequestStatus === 200 && feedbackRequestFlag) {
      showToaster("Thanks for your feedback", "success");
      setFeedbackRequestFlag(false);
      dispatch(getChatLikesDislikeStatus());
    }
    if (chatLikeStatus === 200 && chatStatusData.length > 0) {
      setChatCount(chatStatusData);
    }
  }, [
    feedbackInitialData,
    feedbackServiceRequestStatus,
    chatStatusData,
    chatLikeStatus,
  ]);
  // The storage account backing dataset documents has public network access
  // disabled, so a SAS URL is unreachable directly from the browser (403
  // AuthorizationFailure). Source-document download/preview fetches the
  // bytes through an authenticated backend route instead, same pattern as
  // the AuthenticatedImage/getImageBlob fix for chat-embedded images.
  const handleDownloadChatFile = async (datasetId, filename) => {
    enabledownloadFlagLoader();
    try {
      const blob = await getFileContentBlob(datasetId, filename);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      showToaster("Error downloading file", "error");
    } finally {
      disabledownloadFlagLoader();
    }
  };

  const previewObjectUrlRef = useRef(null);

  const handlePreview = (datasetId, filename, pageNumber) => async () => {
    setPreviewDocFilename(filename);
    setPageNumber(pageNumber);
    try {
      const blob = await getFileContentBlob(datasetId, filename);
      if (previewObjectUrlRef.current) {
        window.URL.revokeObjectURL(previewObjectUrlRef.current);
      }
      const objectUrl = window.URL.createObjectURL(blob);
      previewObjectUrlRef.current = objectUrl;
      setPreviewDocUrl(objectUrl);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error previewing file:", error);
      showToaster("Error downloading file", "error");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        window.URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);

  const handleThubmsModel = (chatConversationId) => {
    setThubmsUpModel(true);
    setConversationId(chatConversationId);
  };

  const handleClose = () => {
    setSpecialChar(true);
    setInputValue("");
    setRadioValues("");
    setIsButtonEnabled(false);
    setThubmsUpModel(false);
  };

  const handleOptionChange = () => { };

  const handleChange = (event) => {
    const value = event.target.value;
    const sanitizedValue = value.replace(/[^\w\s]/gi, "");
    setInputValue(value);
    setSpecialChar(value !== sanitizedValue ? false : true);
    setLengthError(sanitizedValue.length > 150 ? false : true);
    (typeof radioValues !== "undefined" && radioValues !== null) ||
      (lengthError && value == sanitizedValue && sanitizedValue.length > 0)
      ? setIsButtonEnabled(true)
      : setIsButtonEnabled(false);
  };

  const isEmpty = (value) => {
    return (
      value == null || (typeof value === "string" && value.trim().length === 0)
    );
  };

  const handleSelectRadio = (event, { value }) => {
    setRadioValues({ value });
    setIsButtonEnabled(true);
  };

  const handleTableRowClick = (value, text) => {
    setRadioValues({ value });
    setSuggestionTypes({ text });
  };

  const handleThubmsUpPopup = async () => {

    if (isButtonEnabled) {
      handleFeedbackApiRequest(
        conversationId,
        0,
        radioValues ? suggestionTypes.text : "",
        inputValue,
        "dislike"
      );
      setSpecialChar(true);
      setInputValue("");
      setRadioValues("");
      setIsButtonEnabled(false);
      setThubmsUpModel(false);
    }
  };

  // Removed duplicate declaration of setLatestFeedback

  //  Call feedback api request
  const handleFeedbackApiRequest = (
    chatConversationId,
    likeType,
    suggestionOption = "",
    reason = "",
    feedbackType = ""
  ) => {
    try {
      if (!chatConversationId) return;
      // Remove duplicate feedback submission check to allow changing feedback
      setLatestFeedback({ id: chatConversationId, type: likeType }); // Always update latest feedback
      dispatch(feedbackServiceRequest({
        datasetId,
        chatMetaId: chatID,
        chatConversationId,
        likeType,
        suggestionType: suggestionOption,
        reason,
        datasetTitle,
        userQuestion,
        feedbackType,
        aiResponse: text,
      }));
      setFeedbackRequestFlag(true);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
  };


  const copyToClipboard = async () => {
    try {
      showToaster("Successfully copied to clipboard", "success");
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const codeBlockRenderer = ({ node, inline, className, children }) => {
    const language = className ? className.replace("language-", "") : "";
    return !inline ? (
      <SyntaxHighlighter
        language={language || "text"}
        style={dracula}
        showLineNumbers={true}
      >
        {String(children).trim()}
      </SyntaxHighlighter>
    ) : (
      <code className={className}>{children}</code>
    );
  };

  const handleSatisfaction = (isSatisfied) => {
    console.log("User satisfaction:", isSatisfied);
    setFeedbackGiven(true);
    if (isSatisfied) {
      setShowThankYouMessage(true);
      setShowSatisfactionCard(false);
      handleFeedbackApiRequest(chatConversationId, 1);
    } else {
      setShowFeedbackOptions(true);
      setShowSatisfactionCard(false);
      setSelectedFeedbackOption("");
    }
  };

  useEffect(() => {
    if (showFeedbackOptions && sendFeed != null && latestFeedback !== chatConversationId) {
      if (showOtherFeedbackInput) {
        handleFeedbackApiRequest(chatConversationId, 0, selectedFeedbackOption || "Other", sendFeed, "feedback");
      } else {
        handleFeedbackApiRequest(chatConversationId, 0, sendFeed, "", "feedback");
      }
      setLatestFeedback(chatConversationId);
      setSendFeed(null);
      setFeedbackGiven(true);
      console.log("Feedback submitted:", sendFeed);
    }
  }, [showFeedbackOptions, sendFeed, latestFeedback]);

  // ----------------------------
  // Modified rendering in Message component:
  // If text equals "Generating Response...", render LoadingDots.
  // If it's a user question (isQuestion true), render via ReactMarkdown.
  // Otherwise (bot response), render using the Typewriter component for a typing animation.
  // ----------------------------

  // Live Agent Help inline button: when the bot mentions the phrase
  // "Live Agent Help" in its response and the feature is available for
  // this user, render the phrase as a clickable button inside the markdown
  // that triggers the same flow as the existing "Live Agent Help" CTA
  // buttons.
  //
  // Once Live Agent Help is already active for this session
  // (isAgentHelp / isAgentScreen), the click handler is suppressed and the
  // phrase renders as plain text in this and all subsequent bot responses
  // so the user can't trigger it twice. We deliberately keep the markdown
  // link injected even when disabled so the rendered text is stable and
  // the typewriter doesn't restart when the active flag flips.
  const isAgentHelpFeatureAvailable =
    (DocSettingsvalue?.serviceAgentEnabled && successAgentEmail) ||
    currentUserType === "ExternalUser";
  const isAgentHelpDisabled = !!(isAgentHelp || isAgentHelpRequested || isAgentScreen);
  const dynamicMdComponents = useMemo(() => {
    if (isQuestion || !isAgentHelpFeatureAvailable) {
      return mdComponents;
    }
    const anchorRenderer = buildAgentHelpAnchorRenderer(
      () => handleSendQuestion("Agent-Help"),
      { isDisabled: isAgentHelpDisabled }
    );
    if (!anchorRenderer) return mdComponents;
    return { ...mdComponents, a: anchorRenderer };
  }, [isQuestion, isAgentHelpFeatureAvailable, isAgentHelpDisabled, handleSendQuestion]);

  // Pre-process bot text so the phrase "Live Agent Help" becomes a markdown
  // link with our sentinel href, which the `a` override above turns into a
  // clickable button (or plain text when disabled). When the feature is
  // not available for this user we skip the rewrite entirely so the phrase
  // renders as plain prose.
  const renderText = (!isQuestion && isAgentHelpFeatureAvailable)
    ? injectAgentHelpLink(text)
    : text;

  return (
    <>
      {toasterVisible && (
        <CustomToaster
          message={toasterMessage}
          duration={3000}
          onClose={closeToaster}
          position="right-top"
          appearance={toasterAppearance}
        />
      )}
      <PDFViewer
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        url={previewDocUrl}
        filename={PreviewDocFilename}
        pageNumber={pageNumber}
      />
      {text && (
        <div className={`message ${isQuestion ? "question" : "answer"}`}>
          {isQuestion && !isRegenerate && (
            <div className="icons-question">
              <img src="/images/user-chat-icon.svg" alt="chat Icon" />
            </div>
          )}
          <div className="outer-chat-bubble">
            {!isQuestion && (
              <div className="icons-answer" chattestId={chatConversationId}>
                <img src="/images/question-chat-icon.svg" alt="chat Icon" />
              </div>
            )}
            <div className="question-box" data-cy="ai-response-cy">
              <React.Fragment>
                {text === "Generating Response..." ? (
                  (isAgentHelp || isAgentScreen) ? null : <LoadingDots />
                ) : isQuestion ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={mdComponents}
                  >
                    {(() => {
                      const seenNumbers = new Set();
                      return text
                        .replace(/^(\d+)\.\s/, (_, number) => {
                          const currentNumber = parseInt(number, 10);
                          if (!seenNumbers.has(currentNumber)) {
                            seenNumbers.add(currentNumber);
                            return `${currentNumber}. `;
                          } else {
                            return `<span style="margin-left: 20px; font-size: 0.9em;">${currentNumber}.</span> `;
                          }
                        })
                        .replace(/\n\n(\d+)\./g, (_, number) => {
                          const currentNumber = parseInt(number, 10);
                          if (!seenNumbers.has(currentNumber)) {
                            seenNumbers.add(currentNumber);
                            return `\n\n${currentNumber}.`;
                          } else {
                            return `\n\n<span style="margin-left: 20px; font-size: 0.9em; font-weight: bold;">${currentNumber}.</span>`;
                          }
                        })
                        .replace(/(?:^|\n)- /g, '\n• ')
                        .replace(/\n(\s+)/g, (_, spaces) => {
                          const spaceCount = spaces.length * 4;
                          return `\n<span style="margin-left: ${spaceCount}px;">${spaces}</span>`;
                        })
                        .replace(/\n/g, '<br />') + `</pre>`;
                    })()}
                  </ReactMarkdown>
                ) : isInitialGreeting ? (
                  <TypewriterMarkdown text={renderText} speed={30} components={dynamicMdComponents} />
                ) : isFromChatHistory ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={dynamicMdComponents}
                  >
                    {renderText}
                  </ReactMarkdown>
                ) : (
                  <TypewriterMarkdown text={renderText} speed={5} skip={!isLastResponse} onComplete={isLastResponse ? onTypewriterComplete : undefined} components={dynamicMdComponents} />
                )}
              </React.Fragment>
              {(isQuestion || isAgentMessage) && timestamp && text !== "Generating Response..." && (
                <Popup
                  inverted
                  position="top center"
                  content={formatMessageTimestampFull(timestamp)}
                  trigger={
                    <div
                      className="message-timestamp"
                      data-cy="message-timestamp"
                    >
                      {formatMessageTimestamp(timestamp)}
                    </div>
                  }
                />
              )}
              {isQuestion && !isRegenerate ? (
                null
              ) : (
                <>
                  {imageBlobURLs && imageBlobURLs.length > 0 && (
                    <div>
                      {imageBlobURLs.map(
                        (filename, imgIndex) =>
                          filename && (
                            <AuthenticatedImage
                              imgKey={imgIndex}
                              relativePath={filename.trim()} // Trim any extra whitespace
                              apiEndpoint={imageApiEndpoint}
                              style={{
                                maxWidth: "100%",
                                height:
                                  showImagesOption === "thumbnails(fullPages)"
                                    ? "150px"
                                    : "auto",
                                padding: "10px", // Add padding between images
                                boxSizing: "border-box", // Ensure padding doesn't affect width
                                cursor: "zoom-in",
                                border:
                                  showImagesOption === "thumbnails(fullPages)" ||
                                    showImagesOption === "fullPages"
                                    ? "1px solid #f4f4f6"
                                    : "none",
                                margin:
                                  showImagesOption === "thumbnails(fullPages)"
                                    ? "5px"
                                    : "none",
                              }}
                              alt={`image-${imgIndex}`}
                              onClick={() =>
                                openViewer(imageBlobURLs, imageBlobURLs.length > 1 ? imgIndex - 1 : imgIndex)}
                            />
                          )
                      )}
                    </div>
                  )}

                  {showSource !== false && filename !== "" && pages !== "" && (
                    <>
                      {sourceData.length > 0 &&
                        sourceData.map((data, index) => {
                          const filenames = data.imageBlobPath.split(","); // Split the URLs by comma
                          return (
                            <div key={index}>
                              {filenames.map(
                                (filename, imgIndex) =>
                                  filename && (
                                    <img
                                      style={{
                                        maxWidth: "100%",
                                        height: "auto",
                                        padding: "10px", // Add padding between images
                                        boxSizing: "border-box", // Ensure padding doesn't affect width
                                        cursor: "zoom-in",
                                      }}
                                      key={imgIndex}
                                      src={filename.trim()} // Trim any extra whitespace
                                      alt={`image-${imgIndex}`}
                                      onClick={() =>
                                        openViewer(filenames, imgIndex)
                                      }
                                    />
                                  )
                              )}
                            </div>
                          );
                        })}
                      <hr style={{ marginTop: "20px" }} />
                      <Accordion fluid>
                        <Accordion.Title
                          active={activeIndex === 0}
                          index={0}
                          onClick={handleClick}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <h4>Sources</h4>

                          {activeIndex === 0 ? (
                            <FaChevronUp data-cy="ai-assitant-source-open" />
                          ) : (
                            <FaChevronDown data-cy="ai-assitant-source-close" />
                          )}
                        </Accordion.Title>
                        <Accordion.Content active={activeIndex === 0}>
                          {renderCards(
                            sourceData,
                            datasetId,
                            handleDownloadChatFile,
                            handlePreview
                          )}
                        </Accordion.Content>
                      </Accordion>
                    </>
                  )}



                </>
              )}
            </div>
            {!isQuestion && text !== "Generating Response..." && !isInitialGreeting ? (
              <div className="bubble-float-icon">
                <Popup
                  trigger={
                    <img
                      src="/images/copy-icon.svg"
                      alt="chat Icon"
                      onClick={copyToClipboard}
                      data-cy="copytoclipboard-click-cy"
                    />
                  }
                  content={<span>Copy To clipboard</span>
                  }
                  position="top center"
                  inverted
                />
              </div>
            ) : (
              ""
            )}
            {currentUserType === "ExternalUser" && !isQuestion && text !== "Generating Response..." && !isInitialGreeting && isLastResponse && DocSettingsvalue?.serviceAgentEnabled && successAgentEmail && (
              <div className="satisfaction-card" style={{ clear: "both", marginLeft: "20px" }}>
                <Popup
                  inverted
                  position="top center"
                  content="Connect with a live support agent"
                  trigger={
                    <p
                      className="satisfaction-link"
                      onClick={isAgentHelpDisabled ? undefined : () => handleSendQuestion('Agent-Help')}
                      style={{ cursor: isAgentHelpDisabled ? 'not-allowed' : 'pointer', opacity: isAgentHelpDisabled ? 0.5 : 1, pointerEvents: isAgentHelpDisabled ? 'none' : 'auto' }}
                    >
                      Live Agent Help
                    </p>
                  }
                />
              </div>
            )}
          </div>
          {text && isLastResponse && classification.includes("DP2") && (
            <div className="satisfaction-card" style={{ clear: "both", marginLeft: "20px" }}>
              <p style={{ marginTop: "10px", marginBottom: "5px" }}>Are you satisfied with the answer?</p>
              <p
                className="satisfaction-link"
                onClick={feedbackGiven ? undefined : () => handleSatisfaction(true)}
                style={{ cursor: feedbackGiven ? 'not-allowed' : 'pointer', opacity: feedbackGiven ? 0.5 : 1, pointerEvents: feedbackGiven ? 'none' : 'auto' }}
              >Yes</p>
              <p
                className="satisfaction-link"
                onClick={feedbackGiven ? undefined : () => handleSatisfaction(false)}
                style={{ cursor: feedbackGiven ? 'not-allowed' : 'pointer', opacity: feedbackGiven ? 0.5 : 1, pointerEvents: feedbackGiven ? 'none' : 'auto' }}
              >No</p>
              {!isRegenerate && (
                <Popup
                  inverted
                  position="top center"
                  content="Generate new response to your query"
                  trigger={
                    <p className="satisfaction-link" onClick={() => handleRegenerate(userQuestion, chatID)}>Regenerate</p>
                  }
                />
              )}
              {((DocSettingsvalue?.serviceAgentEnabled && successAgentEmail) || currentUserType === "ExternalUser") && (
                <Popup
                  inverted
                  position="top center"
                  content="Connect with a live support agent"
                  trigger={
                    <p
                      className="satisfaction-link"
                      onClick={isAgentHelpDisabled ? undefined : () => handleSendQuestion('Agent-Help')}
                      style={{ cursor: isAgentHelpDisabled ? 'not-allowed' : 'pointer', opacity: isAgentHelpDisabled ? 0.5 : 1, pointerEvents: isAgentHelpDisabled ? 'none' : 'auto' }}
                    >
                      Live Agent Help
                    </p>
                  }
                />
              )}
            </div>
          )}

          {text && isLastResponse && quickResponses && quickResponses.length > 0 && (
            <div className="satisfaction-card" style={{ clear: "both", marginLeft: "20px" }}>
              <p style={{ marginTop: "10px", marginBottom: "5px" }}>Do you want to proceed with ticket creation?</p>
              <div>
                {quickResponses.map((response, index) => (
                  <p key={index} className="satisfaction-link" onClick={() => handleQuickResponseClick(response, chatID)}>
                    {response}
                  </p>
                ))}
              </div>
            </div>
          )}

          {showThankYouMessage && isLastResponse && (
            <div className="thank-you-message" style={{ clear: "both", marginLeft: "20px", marginTop: "10px" }}>
              <p>Thank you for your response!</p>
            </div>
          )}
          {showFeedbackOptions && isLastResponse && classification.includes("DP2") && (
            <div className="feedback-options-card">
              <p>Please provide more details:</p>
              <div>
                {suggestionOptions.map((option, index) => (
                  <p key={index} className={`feedback-link${selectedFeedbackOption === option.title ? " feedback-link-selected" : ""}`} onClick={() => { setSelectedFeedbackOption(option.title); setShowOtherFeedbackInput(true); }}>{option.title}</p>
                ))}
                {((DocSettingsvalue?.serviceAgentEnabled && successAgentEmail) || currentUserType === "ExternalUser") && (
                  <Popup
                    inverted
                    position="top center"
                    content="Connect with a live support agent"
                    trigger={
                      <p
                        className="feedback-link"
                        onClick={isAgentHelpDisabled ? undefined : () => handleSendQuestion('Agent-Help')}
                        style={{ cursor: isAgentHelpDisabled ? 'not-allowed' : 'pointer', opacity: isAgentHelpDisabled ? 0.5 : 1, pointerEvents: isAgentHelpDisabled ? 'none' : 'auto' }}
                      >Live Agent Help</p>
                    }
                  />
                )}
              </div>
            </div>
          )}
          {showOtherFeedbackInput && isLastResponse && classification.includes("DP2") && (
            <div className="other-feedback-input" style={{ clear: "both", marginLeft: "20px", marginTop: "10px" }}>
              <textarea
                rows="3"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Please provide your feedback here..."
                className="custom-ai-input"
                data-cy="feedback-input-cy"
              />
              <div className="middleCenter" data-cy="input-with-icon">
                <Image
                  className="icon-image"
                  src="/images/grey-send.svg"
                  alt="Submit Feedback"
                  onClick={() => handleFeedbackOptionClick(userQuestion, chatID, input)}
                  data-cy="feedback-submit-img-click-cy"
                />
              </div>
            </div>
          )}
        </div>
      )}
      {isViewerOpen &&
        currentFilenames.length > 0 &&
        currentFilenames[currentImageIndex] && (
          <Lightbox
            // lightboxSrcs is resolved by a dedicated effect keyed on
            // isViewerOpen/currentImageIndex/currentFilenames — falls back to
            // a blank placeholder only until that fetch resolves.
            mainSrc={lightboxSrcs.main || BLANK_IMAGE_PLACEHOLDER}
            nextSrc={lightboxSrcs.next || BLANK_IMAGE_PLACEHOLDER}
            prevSrc={lightboxSrcs.prev || BLANK_IMAGE_PLACEHOLDER}
            onCloseRequest={() => closeViewer()}
            onMovePrevRequest={() =>
              setCurrentImageIndex(
                (currentImageIndex + currentFilenames.length - 1) %
                currentFilenames.length
              )
            }
            onMoveNextRequest={() =>
              setCurrentImageIndex(
                (currentImageIndex + 1) % currentFilenames.length
              )
            }
            imagePadding={50} // Padding around the lightbox image
          />
        )}

      <Modal
        open={thubmsUpModel}
        onClose={handleClose}
        size="tiny"
        className="add-document-model filterPopup feedbackPopup"
      >
        <Modal.Content>
          <Header as="h3">
            Please Share your feedback and help us get better
          </Header>
          <Grid>
            {suggestionOptions.length > 0 ? (
              <Grid.Row>
                {suggestionOptions.map((val, index) => {
                  return (
                    <Grid.Column
                      width={8}
                      key={index}
                      className="feedbackFullWidth"
                    >
                      <Form.Field
                        onClick={() => handleTableRowClick(val._id, val.title)}
                        data-cy="suggestion-radio-select-cy"
                      >
                        <Radio
                          className="datasetRadioOption"
                          label={val.title}
                          name="radioGroup"
                          value={val._id}
                          checked={
                            !isEmpty(radioValues)
                              ? radioValues.value === val._id
                              : ""
                          }
                          onChange={handleSelectRadio}
                        />
                      </Form.Field>
                    </Grid.Column>
                  );
                })}
              </Grid.Row>
            ) : (
              <>
                <Grid.Row className="skelton-chat-radio-row">
                  <Grid.Column width={8} className="skelton-chat-radio-column">
                    <Form.Field>
                      <div className="skeleton-view skelton-chat-radio-column-div"></div>
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column width={8} className="skelton-chat-radio-column">
                    <Form.Field>
                      <div className="skeleton-view skelton-chat-radio-column-div"></div>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={8} className="skelton-chat-radio-column">
                    <Form.Field>
                      <div className="skeleton-view skelton-chat-radio-column-div"></div>
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column width={8} className="skelton-chat-radio-column">
                    <Form.Field>
                      <div className="skeleton-view skelton-chat-radio-column-div"></div>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              </>
            )}
          </Grid>
          <br />
          <br />
          <label>Desired response</label>
          <br />
          <Input
            type="text"
            value={inputValue}
            onChange={handleChange}
            placeholder="Feel free to add any specific details"
            className="add-datset-input"
            data-cy="feedback-comments-input-cy"
          />
          <br />
        </Modal.Content>
        <Modal.Actions>
          <button
            className="cancel-btn"
            onClick={handleClose}
            data-cy="feedback-cancel-submit-btn"
          >
            Cancel
          </button>
          <button
            disabled={!isButtonEnabled}
            className="add-btn"
            onClick={handleThubmsUpPopup}
            data-cy="feedback-submit-btn"
          >
            Submit
          </button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

const handleSatisfaction = (isSatisfied) => {
  console.log("User satisfaction:", isSatisfied);
  // Add your logic here to handle user satisfaction
};

const renderCards = (
  sourceData,
  datasetId,
  handleDownloadChatFile,
  handlePreview
) => {
  if (!sourceData || sourceData.length === 0) {
    return null;
  }

  return (
    <Grid columns={3}>
      {sourceData.map((data, index) => {
        const filename = data.source.split("/").pop();
        const pages = data.page;
        const pageNumbers = pages.split(",").map(Number);

        // Extract the correct dataset ID from the source path for quick chat
        // Source format: "datasetId/document.pdf" 
        const sourceDatasetId = data.source.split("/")[0];

        // Remove duplicates
        const uniquePages = [...new Set(pageNumbers)];


        // Group pages into chunks of 7
        const chunkedPages = [];
        for (let i = 0; i < uniquePages.length; i += 7) {
          chunkedPages.push(uniquePages.slice(i, i + 7));
        }

        return (
          <Grid.Column key={index} className="question-box-outer">
            <Card style={{ border: "1px solid #ccc" }}>
              <Card.Content className="question-box-inner">
                <Card.Meta>
                  <p
                    style={{
                      fontSize: "15px",
                      fontWeight: "500",
                      color: "#36475a",
                    }}
                  >
                    {filename.length > 13 ? (
                      <Popup
                        content={filename}
                        inverted
                        position="right center"
                        style={{ borderRadius: "15px" }}
                        trigger={<label>{filename.slice(0, 13) + ".."}</label>}
                      />
                    ) : (
                      filename
                    )}
                  </p>
                  <div style={{ paddingTop: "10px" }} className="greycolor">
                    Pages:
                    <img
                      onClick={handlePreview(sourceDatasetId, filename, 1)}
                      src="/images/preview-grey-icon.svg"
                      alt="Preview"
                    />
                    <img
                      onClick={() =>
                        handleDownloadChatFile(sourceDatasetId, filename)
                      }
                      src="/images/Download.svg"
                      alt="pdf Icon"
                      size="mini"
                      data-cy="ai-assitant-source-download"
                      style={{
                        marginLeft: "10px",
                        cursor: "pointer",
                        float: "right",
                      }}
                    />
                  </div>
                  <div style={{ marginTop: "5px" }}>
                    {chunkedPages.map((chunk, index) => (
                      <div key={index} style={{ marginBottom: "5px" }}>
                        {chunk.map((page) => (
                          <span
                            key={page}
                            onClick={handlePreview(sourceDatasetId, filename, page)}
                            style={{
                              marginRight: "10px",
                              cursor: "pointer",
                              color: "blue",
                              textDecoration: "underline",
                            }}
                          >
                            {page}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </Card.Meta>
              </Card.Content>
            </Card>
          </Grid.Column>
        );
      })}
    </Grid>
  );
};

const MessageList = ({
  messages,
  enabledownloadFlagLoader,
  disabledownloadFlagLoader,
  suggestionOptions,
  chatHistoryIds,
  handleRegenerate,
  handleSendQuestion,
  chatID,
  showImagesOption,
  handleFeedbackOptionClick,
  handleQuickResponseClick,
  sendFeed,
  setSendFeed,
  DocSettingsvalue, // Pass DocSettingsvalue to MessageList
  successAgentEmail,
  showSource,
  onTypewriterComplete,
  isAgentHelp,
  isAgentHelpRequested,
  isAgentScreen,
}) => {

  return (
    <div>
      {messages.map((message, index) => {
        const isLastResponse =
          index === messages.length - 1 && !messages.isQuestion;
        return (
          <Message
            key={index}
            text={message.text}
            isQuestion={message.isQuestion}
            sourceData={message.sourceData}
            imageBlobPaths={message.imageBlobPaths}
            imageBlobURLs={message.imageBlobURLs}
            imageApiEndpoint={message.imageApiEndpoint}
            enabledownloadFlagLoader={enabledownloadFlagLoader}
            disabledownloadFlagLoader={disabledownloadFlagLoader}
            chatConversationId={message.chatConversationId}
            suggestionOptions={suggestionOptions}
            userQuestion={message.userQuestion}
            chatHistoryIds={chatHistoryIds}
            handleRegenerate={handleRegenerate}
            handleSendQuestion={handleSendQuestion}
            chatID={chatID}
            isLastResponse={isLastResponse} // Pass the new prop
            isRegenerate={message.isRegenerate}
            isInitialGreeting={message.isInitialGreeting || false}
            timestamp={message.timestamp}
            isAgentMessage={message.isAgentMessage || false}
            classification={message.classification || ""} // Pass the new prop
            showImagesOption={message.showImagesOption}
            handleFeedbackOptionClick={handleFeedbackOptionClick} // Pass handleFeedbackOptionClick to MessageList
            quickResponses={message.quickResponses} // Pass quickResponses to Message
            handleQuickResponseClick={handleQuickResponseClick} // Pass handleQuickResponseClick to Message
            sendFeed={sendFeed}
            setSendFeed={setSendFeed}
            isFromChatHistory={message.isFromChatHistory || false} // Pass the new prop
            DocSettingsvalue={DocSettingsvalue} // Pass DocSettingsvalue as a prop
            successAgentEmail={successAgentEmail}
            showSource={showSource}
            onTypewriterComplete={isLastResponse ? onTypewriterComplete : undefined}
            isAgentHelp={isAgentHelp}
            isAgentHelpRequested={isAgentHelpRequested}
            isAgentScreen={isAgentScreen}
          />
        );
      })}
    </div>
  );
};
const InputArea = ({ onSendQuestion, disableInputArea, isQuickChatMode, datasetId, showToaster, uploadedFiles, setUploadedFiles, setActiveTab, currentUserType }) => {
  const [input, setInput] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [hasDocumentsInProgress, setHasDocumentsInProgress] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const statusPollingRef = useRef(null);
  const processedDocumentsRef = useRef(new Set()); // Track processed documents
  const dispatch = useDispatch();
  const serachQueryParams = new URLSearchParams(location.search);
  const agentIdFromUrl = serachQueryParams.get("agentId");
  const searchBotDatasetParam = serachQueryParams.get("c");
  const chatTitle = serachQueryParams.get("title");
  const containsSpecialCharactersOrEmpty = (str) => {
    const specialCharactersRegex =
      /^[\p{L}\p{N}\s!"#$%&'()*+,-./:;<=>?@[\\\]_`’“”{|}~]*$/u;
    return specialCharactersRegex.test(str);
  };
  const handleSend = () => {
    dispatch(getDefaultFeedbackSuggestion());
    if (input.trim() !== "") {
      onSendQuestion(input);
      setInput("");
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
      setInput("");
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const autoGrow = (element) => {
    element.style.height = "5px";
    element.style.height = element.scrollHeight + "px";
  };

  // File upload handlers
  const handleAddFileClick = () => {
    // Prevent clicking when uploading or documents are being processed
    if (isUploading || hasDocumentsInProgress) {
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Paste handler for Quick Chat
  const handlePasteClick = async () => {
    try {
      // Check if clipboard API is available
      if (!navigator.clipboard || !navigator.clipboard.readText) {
        showToaster('Clipboard access not available in this browser', 'error');
        return;
      }

      // Read text from clipboard
      const clipboardText = await navigator.clipboard.readText();

      if (clipboardText.trim()) {
        // If there's already text in the input, append with a space
        const newText = input.trim()
          ? `${input.trim()} ${clipboardText.trim()}`
          : clipboardText.trim();

        setInput(newText);
        showToaster('Content pasted successfully', 'success');
      } else {
        showToaster('Clipboard is empty', 'warning');
      }
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      showToaster('Failed to access clipboard. Please ensure clipboard permissions are enabled.', 'error');
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const validation = validateFile(file);
    if (!validation.isValid) {
      showToaster(validation.errors[0], 'error');
      return;
    }

    setIsUploading(true);

    try {
      let currentDatasetId = datasetId;

      // If in quick chat mode and no dataset exists, create one
      if (isQuickChatMode && !currentDatasetId) {
        showToaster('Creating dataset for file upload...', 'info');

        const urlParams = new URLSearchParams(window.location.search);
        const methodParam = urlParams.get('method');
        const quickChatType = methodParam === 'assistantQuickChat' ? 'assistantQuickChat' : 'quickChat';

        const result = await createQuickChatDataset(quickChatType);
        currentDatasetId = result.datasetId;

        // Update the URL to include the new dataset ID
        const newUrl = new URL(window.location);
        newUrl.searchParams.set('c', currentDatasetId);
        window.history.replaceState({}, '', newUrl.toString());

        showToaster(`Dataset created: ${result.datasetName}`, 'success');
      }

      if (!currentDatasetId) {
        throw new Error('No dataset ID available for file upload');
      }

      // Detect quick chat type from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const methodParam = urlParams.get('method');
      const quickChatType = methodParam === 'assistantQuickChat' ? 'assistantQuickChat' : 'quickChat';

      console.log('File upload - detected quick chat type:', quickChatType);

      const uploadedFile = await uploadFileToDataset(file, currentDatasetId, null, false, quickChatType);
      setUploadedFiles(prev => [...prev, uploadedFile]);
      showToaster('File uploaded successfully', 'success');

      // Clear processed documents set for new uploads so we show success messages
      if (uploadedFile._id && !processedDocumentsRef.current.has(uploadedFile._id)) {
        // This ensures we'll show success message when this specific document completes training
        console.log(`Tracking new upload: ${uploadedFile.name} (ID: ${uploadedFile._id})`);
      }

      // Automatically show Files tab when upload starts
      if (setActiveTab) {
        setActiveTab('files');
      }

      // Set documents in progress state
      setHasDocumentsInProgress(true);

      // Start status polling for all documents in dataset
      startStatusPolling();

    } catch (error) {
      console.error('File upload failed:', error);

      // Check if error is due to duplicate file name
      if (error.message && error.message.includes('already exists')) {
        showToaster(error.message, 'error');
      } else {
        showToaster('Failed to upload file', 'error');
      }
    } finally {
      setIsUploading(false);
      // Clear file input
      event.target.value = '';
    }
  };

  const startStatusPolling = () => {
    // Clear existing polling
    if (statusPollingRef.current) {
      statusPollingRef.current.stopPolling();
    }

    // Create new DocumentStatusPoller instance
    statusPollingRef.current = new DocumentStatusPoller(
      datasetId,
      (documents) => {
        // Update uploaded files based on document training status and remove deleted ones
        setUploadedFiles(prevFiles => {
          const updatedFiles = [];

          prevFiles.forEach(file => {
            const fileId = file._id || file.id;
            const documentUpdate = documents.find(doc =>
              doc.name === file.name || doc.id === file.id || doc.id === file._id
            );

            if (documentUpdate) {
              // Document still exists, update its status
              updatedFiles.push({
                ...file,
                _id: documentUpdate.id, // Store the actual document ID from API
                status: documentUpdate.status,
                trainingStatus: documentUpdate.trainingStatus,
                progress: documentUpdate.progress,
                error: documentUpdate.error
              });
            } else {
              // Document not found in API response
              // Check if file has an ID (meaning it was uploaded) - if so, it was deleted
              if (fileId) {
                console.log(`Document ${file.name} (ID: ${fileId}) no longer exists in API response - removing from list`);
                // Show success message for deletion
                showToaster(`Document "${file.name}" deleted successfully`, 'success');
                // Don't add to updatedFiles (effectively removing it)
              } else {
                // File doesn't have an ID yet (still uploading), keep it
                updatedFiles.push(file);
              }
            }
          });

          return updatedFiles;
        });

        // Check if documents are still in progress (exclude deleting documents from blocking uploads)
        const progressInfo = DocumentStatusPoller.getTrainingProgress(documents);
        const uploadBlockingProgress = documents.some(doc =>
          doc.trainingStatus === 'Queued' || doc.trainingStatus === 'Training'
        );
        setHasDocumentsInProgress(uploadBlockingProgress);

        // Show status updates only for newly completed/failed documents
        const newlyCompleted = documents.filter(doc =>
          doc.trainingStatus === 'Trained' && !processedDocumentsRef.current.has(doc.id)
        );
        const newlyFailed = documents.filter(doc =>
          doc.trainingStatus === 'Failed' && !processedDocumentsRef.current.has(doc.id)
        );

        // Mark these documents as processed
        newlyCompleted.forEach(doc => processedDocumentsRef.current.add(doc.id));
        newlyFailed.forEach(doc => processedDocumentsRef.current.add(doc.id));

        // Show status updates only for newly processed documents
        if (newlyCompleted.length > 0) {
          newlyCompleted.forEach(doc => {
            showToaster(`Document "${doc.name}" training completed successfully`, 'success');
          });
        }

        if (newlyFailed.length > 0) {
          newlyFailed.forEach(doc => {
            showToaster(`Document "${doc.name}" training failed`, 'error');
          });
        }
      },
      5000 // Poll every 5 seconds
    );

    // Start polling
    statusPollingRef.current.startPolling();
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));

    // Check if we should stop polling - if no more files are being processed
    const remainingFiles = uploadedFiles.filter(file => file.id !== fileId);
    const activeFiles = remainingFiles.filter(
      file => file.status === 'queued' || file.status === 'processing'
    );

    if (activeFiles.length === 0 && statusPollingRef.current) {
      statusPollingRef.current.stopPolling();
      statusPollingRef.current = null;
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (statusPollingRef.current) {
        statusPollingRef.current.stopPolling();
      }
    };
  }, []);

  return (
    <div className="asAssistantSearchBox">
      <div className="input-with-icon asAssistantSearch">
        {!disableInputArea && (
          <>
            {/* File upload button for Quick Chat - positioned like send button but on left */}
            {isQuickChatMode && (
              <div className="middleLeft" data-cy="file-upload-icon">
                <Popup
                  trigger={
                    <div
                      className="add-file-icon"
                      onClick={handleAddFileClick}
                      data-cy="add-file-btn"
                      style={{
                        width: '32px',
                        height: '32px',
                        cursor: (isUploading || hasDocumentsInProgress) ? 'not-allowed' : 'pointer',
                        opacity: (isUploading || hasDocumentsInProgress) ? 0.8 : 1,
                        background: hasDocumentsInProgress ? '#e3f2fd' : 'white',
                        borderRadius: '50%',
                        border: hasDocumentsInProgress ? '1px solid #2196f3' : '1px solid #dee2e6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                        color: hasDocumentsInProgress ? '#2196f3' : '#0053ed',
                        fontWeight: 'bold',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isUploading || hasDocumentsInProgress ? (
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid #f3f3f3',
                            borderTop: '2px solid #2196f3',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}
                        />
                      ) : (
                        '+'
                      )}
                    </div>
                  }
                  content={
                    isUploading
                      ? "Uploading file..."
                      : hasDocumentsInProgress
                        ? "Processing documents..."
                        : "Attach file"
                  }
                  position="top center"
                  inverted
                />
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.pptx,.png,.jpg,.jpeg,.md"
                  data-cy="file-input"
                />
              </div>
            )}

            {/* Paste button for Quick Chat - positioned next to file upload button */}
            {isQuickChatMode && (
              <div className="middleLeft" data-cy="paste-button" style={{ left: '50px' }}>
                <Popup
                  trigger={
                    <div
                      className="paste-button-icon"
                      onClick={handlePasteClick}
                      data-cy="paste-btn"
                      style={{
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer',
                        opacity: 1,
                        background: 'white',
                        borderRadius: '50%',
                        border: '1px solid #dee2e6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        color: '#0053ed',
                        fontWeight: 'normal',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      📋
                    </div>
                  }
                  content="Paste from clipboard"
                  position="top center"
                  inverted
                />
              </div>
            )}

            <textarea
              ref={textareaRef}
              rows="1"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                // Auto-resize: shrink then grow to fit content (cap at 7 lines)
                e.target.style.height = 'auto';
                const newHeight = Math.min(e.target.scrollHeight, 178);
                e.target.style.height = newHeight + 'px';
                e.target.style.overflowY = e.target.scrollHeight > 178 ? 'auto' : 'hidden';
              }}
              placeholder={agentIdFromUrl ? "Answer Question or Click 'Stop Live Agent' to End Session" : "Ask question"}
              onKeyPress={handleKeyPress}
              className={`custom-ai-input ${isQuickChatMode ? 'with-file-btn' : ''}`}
              data-cy="question-ask-input-cy"
              disabled={disableInputArea}
            />
            <div className="middleCenter" data-cy="input-with-icon">
              <Image
                className="icon-image"
                src="/images/grey-send.svg"
                alt="New Chat"
                onClick={handleSend}
                data-cy="response-img-click-cy"
              />
            </div>
            {/* Dataset title box for non-quick chat mode */}
            {!isQuickChatMode && (
              <div className="ai-dataset-box">
                {chatTitle && chatTitle.length > 10 ? (
                  <Popup
                    content={chatTitle}
                    inverted
                    position="right center"
                    style={{ borderRadius: "15px" }}
                    trigger={<span>{chatTitle.slice(0, 10)}..</span>}
                  />
                ) : (
                  chatTitle
                )}
              </div>
            )}
          </>
        )}

        {currentUserType !== 'HumanAgent' && (
          <div>
            <StarRating />
          </div>
        )}
        {currentUserType !== 'HumanAgent' && (
          <p style={{ color: '#888', fontSize: '11px', textAlign: 'center', margin: '4px 0 0 0', lineHeight: '1.4' }}>
            Innferre is AI and can make mistakes. Please double-check responses.
          </p>
        )}
      </div>
    </div>
  );
};

const ChatBot = ({ checkChatHistoryCreated, changeParams, disableInputArea, isHandleViewCalled, isAssistantDataset, assistantParams, onUploadedFilesChange, setActiveTab, chatViewPreference = "same-side", onAgentActiveChange, onAgentRequestStatusChange, quitAgentFnRef }) => {
  const [messages, setMessages] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const dispatch = useDispatch();
  const userToken = useSelector((state) => state.auth.token);
  const currentUserType = useSelector((state) => state.auth.user);
  const userName = userToken ? userToken.name : "";
  const userRole = userToken ? userToken.extension_Role : "";
  const greetingAddedRef = useRef(false);
  const [QuestionText, setQuestionText] = useState("");
  const [chatFlag, setchatFlag] = useState(false);
  const [newChatFlag, setnewChatFlag] = useState(false);
  const [newQueryParam, setnewQueryParam] = useState(null);
  const [doclistLength, setDoclistLength] = useState(false);
  const [threadId, setThreadId] = useState("");
  const [isAgentHelp, setIsAgentHelp] = useState(false);
  const [agentRequestFailed, setAgentRequestFailed] = useState(false);
  const [agentPollingInterval, setAgentPollingInterval] = useState(null);
  let trigger = useNavigate();
  const isMounted = useRef(true);
  const messagesDivRef = useRef(null);
  const waitingForResponseRef = useRef(false);
  const [responseConversationID, setResponseConversationID] = useState("");

  // Notify parent when uploadedFiles change
  useEffect(() => {
    if (onUploadedFilesChange) {
      console.log('ChatBot: uploadedFiles changed, notifying parent:', uploadedFiles);
      onUploadedFilesChange(uploadedFiles);
    }
  }, [uploadedFiles, onUploadedFilesChange]);

  const { loading, chatData, chatDataStatus } = useSelector(
    (state) => state.ChatHistoryConversation || {}
  );
  const [DocListData, setDocListData] = useState([]);
  const [documentSummarizationDataArray, setDocumentSummarizationDataArray] =
    useState([]);
  const { successConversationData, successConversationDataStatus } =
    useSelector((state) => state.saveSuccessChatConversation || {});

  const { documentSummarizationData, documentSummarizationStatus } =
    useSelector((state) => state.documentSummarizationResponse || {});
  const { qnaData, qnaDataStaus } = useSelector(
    (state) => state.qnaChatBot || {}
  );
  const { chatHistoryData, registerChatStatus } = useSelector(
    (state) => state.qnaChatHistory || {}
  );
  const { docListData, docListDataStatus } = useSelector(
    (state) => state.getDocReducer || {}
  );

  const { suggestionData, suggestionStatus } = useSelector(
    (state) => state.feedbackSuggestionService || {}
  );

  const { chatStatusData, chatLikeStatus } = useSelector(
    (state) => state.chatStatusLikeDislike || {}
  );

  const { docSettings, UpdateDocSettingsStatus } = useSelector(
    (state) =>
      state.fetchdocSettings || {
        docSettings: {},
        loading: false,
        error: null,
        UpdateDocSettingsStatus: null,
      }
  );

  const { parameters, loading: parametersLoading, error } = useSelector(
    (state) =>
      state.parameters || { parameters: {}, loading: false, error: null }
  );


  const assistantParameters = useSelector(
    (state) => state.assistantParameters?.parameters || {}
  );

  const { agentData, error: createAgentError } = useSelector(
    (state) => state.createAgent || {}
  );

  // If registering the live agent request fails, no request exists: tell the user and allow them to ask again.
  useEffect(() => {
    if (createAgentError) {
      setIsAgentHelp(false);
      setAgentRequestFailed(true);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          isQuestion: false,
          text: "We could not connect you to a live agent right now. Please try again.",
          isAgentMessage: true,
        },
      ]);
    }
  }, [createAgentError]);

  const { statusData, agentStatus } = useSelector((state) => state.getAgentStatus || {});

  const { successAgentValidation, agentValid } = useSelector(
    (state) => state.validateAgent || {}
  );

  const { successAgentEmail, AgentEmailId } = useSelector(
    (state) => state.getAgentEmail || { successAgentEmail: false, AgentEmailId: null }
  );

  const { updateStatus } = useSelector((state) => state.updateAgentStatus || {});

  const { question, answer, type, questionTimestamp, answerTimestamp } = useSelector((state) => state.getHumanAgentQna || {});
  // Track last-handled per-field timestamp so identical message text sent
  // multiple times still produces a new render entry on user/agent screen.
  const lastHandledQuestionTsRef = useRef(null);
  const lastHandledAnswerTsRef = useRef(null);

  const { UserQuestion, AgentResponse, responseType } = useSelector((state) => state.createHumanAgentQna || {});

  // Organization details for usage settings
  const orgDetails = useSelector((state) => state.admin?.details);

  // Add datasets selector for reference dataset functionality
  const { response: allDatasets } = useSelector((state) => state.getlistDatasetReducer || {});

  const location = useLocation();
  const history = createBrowserHistory();
  const queryParams = new URLSearchParams(location.search);
  const chatBotQueryParam = queryParams.get("cid");
  // chatData/chatDataStatus only get reset once the fetch-data effect further below
  // actually dispatches a request for the new cid — and that dispatch can lag behind
  // the cid change by more than one render (e.g. other unrelated prop/state updates
  // can force extra renders first). So "did cid just change since last render" isn't
  // enough — we track, via a ref set ONLY inside that fetch-triggering effect,
  // whether a request has actually been kicked off for the cid we're currently on.
  // Until it has, chatData/chatDataStatus are guaranteed to still belong to whatever
  // chat was previously loaded, no matter how many renders occur in between.
  const lastRequestedChatIdRef = useRef(null);
  const isDataConfirmedForCurrentChat = lastRequestedChatIdRef.current === chatBotQueryParam;
  // True once a fetch for this cid has definitively completed (post-retry) with
  // zero saved messages — almost always means the user left before the assistant
  // replied, so the question/answer pair was never persisted.
  const isConfirmedEmptyConversation =
    !!chatBotQueryParam &&
    isDataConfirmedForCurrentChat &&
    !loading &&
    chatDataStatus === 200 &&
    Array.isArray(chatData) &&
    chatData.length === 0;
  const chatBotDatasetParam = queryParams.get("c");
  const datasetParamttile = queryParams.get("title");
  const datasetSummarization = queryParams.get("summarization");
  const quickChatParam = queryParams.get("quickChat");
  const methodParam = queryParams.get("method");

  // Check if user is coming from quick chat
  const isQuickChatMode = quickChatParam === "1" && (methodParam === "quickChat" || methodParam === "assistantQuickChat");
  const [showLoader, setShowLoader] = useState(false);
  const [historyLoadingTakingLong, setHistoryLoadingTakingLong] = useState(false);
  const [summarizationFlag, setSummarizationFlag] = useState(false);
  const [downloadFlagLoader, setdownloadFlagLoader] = useState(false);
  const [suggestionOptions, setSuggestionOptions] = useState([]);
  const [countQueryFlag, setCountQueryFlag] = useState(false);
  const [chatHistoryIds, setChatHistoryIds] = useState("");
  const [qnastore, setQnastore] = useState("");
  const [DocSettingsvalue, setDocSettingsvalue] = useState([]);
  const [promptvalue, setPromptvalue] = useState([]);
  const [assistantParametersValue, setAssistantParametersValue] = useState([]);
  const [sendFeed, setSendFeed] = useState(null);
  const [initialParametersLoaded, setInitialParametersLoaded] = useState(false);
  const [parametersLoadTimeout, setParametersLoadTimeout] = useState(null);
  const agentIdFromUrl = queryParams.get("agentId");
  const isHumanAgentHistoryView =
    currentUserType === "HumanAgent" && queryParams.get("historyView") === "1";
  //const [agentIdFromUrl, setAgentIdFromUrl] = useState(queryParams.get("agentId"));
  let isAgentScreen = !!agentIdFromUrl;
  const [ValidatedAgentStatus, setValidatedAgentStatus] = useState("");
  const [currentAgentStatus, setCurrentAgentStatus] = useState('Open');
  const [previousAgentResponse, setPreviousAgentResponse] = useState(null);
  const [AgentId, setAgentId] = useState(null);
  const [agentHelp, setAgentHelp] = useState(false);
  const [agentLeft, setAgentLeft] = useState(false);
  const [agentJoined, setAgentJoined] = useState(false);
  const [agentSessionTimedOut, setAgentSessionTimedOut] = useState(false);
  const [latestResponse, setLatestResponse] = useState("");
  const [toasterVisible, setToasterVisible] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterAppearance, setToasterAppearance] = useState("success");

  // Live Agent Help modal state (collect email and company name)
  const [showAgentHelpModal, setShowAgentHelpModal] = useState(false);
  const [agentHelpUserEmail, setAgentHelpUserEmail] = useState("");
  const [agentHelpCompanyName, setAgentHelpCompanyName] = useState("");
  const [agentHelpModalError, setAgentHelpModalError] = useState("");

  // Empty-conversation modal state (shown when a chat resolves with no saved messages)
  const [showEmptyChatModal, setShowEmptyChatModal] = useState(false);
  const [isDeletingEmptyChat, setIsDeletingEmptyChat] = useState(false);
  const emptyChatPromptedRef = useRef(null); // cid we've already prompted for, so it only shows once per chat
  const { deleteChatHistoryResponseData, deleteChatHistoryStatus } = useSelector(
    (state) => state.deleteChatHistory || {}
  );

  const showToaster = (message, appearance) => {
    setToasterVisible(true);
    setToasterMessage(message);
    setToasterAppearance(appearance);
  };

  const closeToaster = () => {
    setToasterVisible(false);
  };


  useEffect(() => {
    if (!showLoader) {
      setHistoryLoadingTakingLong(false);
      return;
    }
    const longTimer = setTimeout(() => {
      setHistoryLoadingTakingLong(true);
    }, 10000);

    return () => clearTimeout(longTimer);
  }, [showLoader]);

  useEffect(() => {
    // chatData/chatDataStatus can't be trusted until a request has actually been
    // dispatched for the chat we're currently viewing (see isDataConfirmedForCurrentChat).
    if (!isDataConfirmedForCurrentChat) {
      return;
    }
    if (showLoader && !loading) {
      if (chatData && chatData.length > 0) {
        setShowLoader(false);
      } else if (chatData && chatData.length === 0 && chatDataStatus === 200) {
        // Fetch has definitively completed (post-retry) with no saved messages —
        // most commonly because the user left before the assistant replied.
        setShowLoader(false);
      }
    }
  }, [loading, chatData, showLoader, chatDataStatus, isDataConfirmedForCurrentChat]);

  // Clear the "already prompted" guard whenever we navigate to a different
  // chat, so returning later to a chat we previously saw as empty (e.g. after
  // visiting other chats in between) can prompt again instead of being
  // silently skipped as "already shown" from that earlier visit.
  useEffect(() => {
    emptyChatPromptedRef.current = null;
  }, [chatBotQueryParam]);

  // Prompt the user once per chat when it resolves as definitively empty —
  // giving them the choice to delete the orphaned chat or keep using it.
  useEffect(() => {
    if (!chatBotQueryParam) {
      setShowEmptyChatModal(false);
      return;
    }
    if (isConfirmedEmptyConversation) {
      if (emptyChatPromptedRef.current !== chatBotQueryParam) {
        emptyChatPromptedRef.current = chatBotQueryParam;
        setShowEmptyChatModal(true);
      }
    } else {
      setShowEmptyChatModal(false);
    }
  }, [chatBotQueryParam, isConfirmedEmptyConversation]);

  const handleContinueEmptyChat = () => {
    setShowEmptyChatModal(false);
  };

  const handleDeleteEmptyChat = () => {
    if (!chatBotQueryParam || isDeletingEmptyChat) return;
    setIsDeletingEmptyChat(true);
    dispatch(deleteChatHistoryData(chatBotQueryParam));
  };

  // React to the delete-chat request we (not the sidebar) initiated
  useEffect(() => {
    if (!isDeletingEmptyChat) return;
    if (deleteChatHistoryResponseData !== undefined && deleteChatHistoryStatus === 200) {
      setIsDeletingEmptyChat(false);
      setShowEmptyChatModal(false);
      dispatch(DeleteChatHistorySuccess([])); // reset shared slice so the sidebar's own listener doesn't re-trigger on this
      showToaster("Chat deleted successfully", "success");
      trigger("/aiassistant");
    } else if (deleteChatHistoryStatus && deleteChatHistoryStatus !== 200) {
      setIsDeletingEmptyChat(false);
      showToaster("Failed to delete chat. Please try again.", "error");
    }
  }, [deleteChatHistoryResponseData, deleteChatHistoryStatus, isDeletingEmptyChat]);

  useEffect(() => {
    dispatch(fetchDocSettings(chatBotDatasetParam));
  }, [chatBotDatasetParam]);

  // Fetch datasets for reference dataset functionality
  useEffect(() => {
    if (isQuickChatMode) {
      dispatch(getDatasets());
    }
  }, [isQuickChatMode]);

  useEffect(() => {
    if (chatBotDatasetParam) {
      setInitialParametersLoaded(false); // Reset when dataset changes

      // Clear any existing timeout
      if (parametersLoadTimeout) {
        clearTimeout(parametersLoadTimeout);
      }

      // Set a timeout to allow interaction after 5 seconds even if parameters haven't loaded
      const timeoutId = setTimeout(() => {
        console.log('ChatBot: Parameters load timeout reached, allowing user interaction with default values');
        setInitialParametersLoaded(true);
      }, 5000);

      setParametersLoadTimeout(timeoutId);
      dispatch(fetchDataSetParameters(chatBotDatasetParam));
    }
  }, [chatBotDatasetParam]);

  useEffect(() => {
    // Guard: Only fetch if we have a valid dataset ID and it's an assistant dataset
    if (isAssistantDataset && chatBotDatasetParam) {
      setInitialParametersLoaded(false); // Reset when assistant dataset changes
      dispatch(fetchAssistantParameters(chatBotDatasetParam));
    }
  }, [chatBotDatasetParam, isAssistantDataset]);


  useEffect(() => {
    if (docSettings && docSettings.length > 0) {
      const settings = docSettings[0]; // Access the first element of the array
      setDocSettingsvalue(settings);
      dispatch(fetchDocSettingsSuccess([]));
    }
  }, [docSettings]);

  useEffect(() => {
    if (assistantParameters && assistantParameters.length > 0) {
      const settings = assistantParameters[0]; // Access the first element of the array
      setAssistantParametersValue(settings);
      if (isAssistantDataset) {
        setInitialParametersLoaded(true);

        // Clear the timeout since parameters loaded successfully
        if (parametersLoadTimeout) {
          clearTimeout(parametersLoadTimeout);
          setParametersLoadTimeout(null);
        }
      }
    }
  }, [assistantParameters, isAssistantDataset, parametersLoadTimeout]);

  useEffect(() => {
    if (parameters && parameters.prompt) {
      // console.log('ChatBot: Regular dataset parameters loaded successfully:', parameters);
      setPromptvalue(parameters);
      setInitialParametersLoaded(true);

      // Clear the timeout since parameters loaded successfully
      if (parametersLoadTimeout) {
        clearTimeout(parametersLoadTimeout);
        setParametersLoadTimeout(null);
      }
    }
  }, [parameters, parametersLoadTimeout]);

  useEffect(() => {
    setQuestionText("");
    dispatch(QnaChatbotSuccess([]));
  }, [showLoader]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (parametersLoadTimeout) {
        clearTimeout(parametersLoadTimeout);
      }
    };
  }, [parametersLoadTimeout]);

  useEffect(() => {
    if (successConversationData && successConversationDataStatus === 200) {
      // Only patch the chatConversationId onto the already-displayed message
      setMessages((prevMessages) => {
        const updated = [...prevMessages];
        // Find the last bot response that has an empty chatConversationId
        for (let i = updated.length - 1; i >= 0; i--) {
          if (!updated[i].isQuestion && updated[i].chatConversationId === "") {
            updated[i] = {
              ...updated[i],
              chatConversationId: successConversationData.id,
            };
            break;
          }
        }
        return updated;
      });
      setQnastore("");
    }
  }, [successConversationData, successConversationDataStatus]);

  useEffect(() => {
    if (userName && !greetingAddedRef.current) {
      const greetingText = isQuickChatMode
        ? `Hey ${userName}, I can help you answer any questions, summarize meeting notes, write content, solve problems, and much more. What would you like to explore today?`
        : `Hey ${userName}, how may I help you today?`;

      setMessages((prevMessages) => [
        {
          isQuestion: false,
          text: greetingText,
          sourceData: [],
          imageBlobPaths: [],
          imageBlobURLs: [],
          chatConversationId: "",
          userQuestion: "",
          threadId: "",
          isInitialGreeting: true, // Mark this message as the initial greeting
        },
        ...prevMessages,
      ]);
      greetingAddedRef.current = true; // Prevent future additions
    }
  }, [userName, isQuickChatMode]);

  useEffect(() => {
    if (chatBotQueryParam) {
      // Mark that a request has now actually been kicked off for this exact chat —
      // gates isConfirmedEmptyConversation/message-building/showLoader-clearing so
      // they never act on the PREVIOUS chat's leftover chatData/chatDataStatus.
      lastRequestedChatIdRef.current = chatBotQueryParam;
      fetchData(chatBotQueryParam);
      setShowLoader(true);
    } else {
      dispatch(resetChatHistoryConversation());
    }
  }, [chatBotQueryParam]);

  // Reset messages and greeting when navigating to a new chat (dataset changes without cid)
  useEffect(() => {
    if (chatBotDatasetParam && !chatBotQueryParam) {
      // Clear messages for new chat
      setMessages([]);
      // Reset greeting ref so greeting can be added again
      greetingAddedRef.current = false;
      // Reset other state for new chat
      setnewQueryParam(null);
      setThreadId("");
    }
  }, [chatBotDatasetParam, chatBotQueryParam]);

  useEffect(() => {
    if (datasetSummarization) {
      setSummarizationFlag(true);
    } else {
      setSummarizationFlag(false);
    }
  }, [datasetSummarization]);

  useEffect(() => {
    if (docListDataStatus === 200) {
      setDocListData(docListData);
      dispatch(getDocumentSuccess([]));
    }
  }, [docListData, docListDataStatus]);

  useEffect(() => {
    if (documentSummarizationStatus === 200) {
      updateDocumentSummarizationData(documentSummarizationData);
      dispatch(DocumentSummarizationSuccess([]));
    }
  }, [documentSummarizationData, documentSummarizationStatus]);

  useEffect(() => {
    if (isMounted.current && chatBotDatasetParam) {
      dispatch(getDocumentsDetails(chatBotDatasetParam));
    }
  }, [chatBotDatasetParam, isMounted]);

  useEffect(() => {
    if (DocListData && DocListData.length > 0 && datasetSummarization) {
      setDoclistLength(DocListData.length);
      var firstFiveDocs = [];
      firstFiveDocs = DocListData;
      dispatch(DocumentSummarizationSuccess([]));
      setDocumentSummarizationDataArray([]);
      firstFiveDocs.map((docData) => {
        getDocSummery(docData);
      });
    }
  }, [DocListData, datasetSummarization]);

  useEffect(() => {
    dispatch(getDefaultFeedbackSuggestion());
  }, []);

  const enabledownloadFlagLoader = () => {
    setdownloadFlagLoader(true);
  };

  const disabledownloadFlagLoader = () => {
    setdownloadFlagLoader(false);
  };

  const getDocSummery = (docData) => {
    const datasetId = docData.datasetId;
    const documentName = docData.documentName;
    dispatch(getDocumantSummerization(datasetId, documentName));
  };

  // response for new chat/register chat
  useEffect(() => {
    if (chatHistoryData && registerChatStatus === 200) {
      if (chatBotQueryParam) {
        queryParams.set("cid", chatHistoryData.id);
      } else {
        queryParams.append("cid", chatHistoryData.id);
      }
      const updatedUrl = `${location.pathname}?${queryParams.toString()}`;
      history.push(updatedUrl);
      setnewChatFlag(true);
      setnewQueryParam(chatHistoryData.id);
      handleSendQuestionToChatbot(QuestionText, chatHistoryData.id, false);

      dispatch(createChatHistoryDataSuccess([]));
      checkChatHistoryCreated(true);
    }
  }, [chatHistoryData, registerChatStatus]);

  // chat history conversation
  useEffect(() => {
    // chatData/chatDataStatus can't be trusted until a request has actually been
    // dispatched for the chat we're currently viewing (see isDataConfirmedForCurrentChat).
    if (!isDataConfirmedForCurrentChat) {
      return;
    }

    const newMessages = [];
    const greetingMessage = {
      isQuestion: false,
      text: isQuickChatMode
        ? `Hey ${userName}, I can help you answer any questions, summarize meeting notes, write content, solve problems, and much more. What would you like to explore today?`
        : `Hey ${userName}, how may I help you today?`,
      sourceData: [],
      imageBlobPaths: [],
      imageBlobURLs: [],
      chatConversationId: "",
      userQuestion: "",
      threadId: "",
      isInitialGreeting: true, // Mark this message as the initial greeting
    };

    if (Array.isArray(chatData) && chatData.length > 0) {
      for (let i = 0; i < Math.max(chatData.length); i++) {
        // On agent screen, skip both the question and bot response for "Agent-Help" entries
        if (isAgentScreen && chatData[i].userQuestion === 'Agent-Help') {
          continue;
        }
        if (chatData[i].botResponse || chatData[i].botResponse === "") {
          // Transform "Agent-Help" to friendly message for display
          const displayText = chatData[i].userQuestion === 'Agent-Help'
            ? 'Live Agent Help requested.'
            : chatData[i].userQuestion;
          newMessages.push({
            text: displayText,
            isQuestion: true,
            sourceData: [],
            isRegenerate: false,
            showImagesOption: chatData[i].showImagesOption,
            imageBlobPaths: [],
            imageBlobURLs: [],
            chatConversationId: chatData[i].id,
            userQuestion: chatData[i].userQuestion,
            threadId: chatData[i].threadId ? chatData[i].threadId : "",
            isFromChatHistory: true, // Set the flag for chat history
            timestamp:
              chatData[i].questionTimestamp ||
              chatData[i].createdUTC ||
              chatData[i].updatedUTC ||
              chatData[i].createdAt ||
              chatData[i].created_at ||
              chatData[i].timestamp ||
              null,
          });
        }
        if (chatData[i].userQuestion || chatData[i].userQuestion === "") {
          // This is from the chat conversation history
          newMessages.push({
            text: chatData[i].botResponse,
            isQuestion: false,
            sourceData: chatData[i].source,
            isRegenerate: chatData[i].isRegenerated,
            showImagesOption: chatData[i].showImagesOption,
            imageBlobPaths: chatData[i].imageBlobPaths
              ? chatData[i].imageBlobPaths
              : [],
            imageBlobURLs: chatData[i].chatMeta.imageBlobURLs
              ? chatData[i].chatMeta.imageBlobURLs
              : [],
            // These relative "images/<path>" entries came from chat-service history,
            // so authenticated fetches must be prefixed with the chat-service API path.
            imageApiEndpoint: process.env.REACT_APP_CHAT_API_ENDPOINT,
            chatConversationId: chatData[i].id,
            userQuestion: chatData[i].userQuestion,
            threadId: chatData[i].threadId ? chatData[i].threadId : "",
            quickResponses: chatData[i].chatMeta.quickResponses || [], // Safely access quickResponses
            isFromChatHistory: true, // Set the flag for chat history
            // Human-agent reply rows have an empty userQuestion + non-empty botResponse; tag them so the timestamp pill renders after refresh.
            isAgentMessage: !chatData[i].userQuestion && !!chatData[i].botResponse,
            timestamp:
              chatData[i].answerTimestamp ||
              chatData[i].updatedUTC ||
              chatData[i].createdUTC ||
              chatData[i].updatedAt ||
              chatData[i].createdAt ||
              chatData[i].created_at ||
              chatData[i].timestamp ||
              null,
          });
        }
      }
    }

    if (!newMessages.some((msg) => msg.isInitialGreeting)) {
      newMessages.unshift(greetingMessage);
    }
    setMessages(newMessages);
  }, [chatData, loading, chatBotQueryParam, chatDataStatus, isDataConfirmedForCurrentChat]);

  // Stamp newly-created messages (user/bot/agent) with a local capture time so
  // every bubble can show when it occurred. Historical messages keep whatever
  // timestamp the backend returned; greetings and the loading placeholder are
  // intentionally skipped.
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    let changed = false;
    const stamped = messages.map((m) => {
      if (
        m &&
        !m.timestamp &&
        !m.isInitialGreeting &&
        !m.isFromChatHistory &&
        !m.isAgentLoading &&
        m.text !== "Generating Response..."
      ) {
        changed = true;
        return { ...m, timestamp: new Date().toISOString() };
      }
      return m;
    });
    if (changed) setMessages(stamped);
  }, [messages]);

  useEffect(() => {
    if (messagesDivRef.current) {
      const container = messagesDivRef.current;
      const lastMessage = messages[messages.length - 1];
      const isWaitingForResponse = lastMessage && !lastMessage.isQuestion && lastMessage.text === "Generating Response...";

      if (isWaitingForResponse) {
        // Mark that we're waiting — keep this true until the next question is sent
        waitingForResponseRef.current = true;
        // Remove any leftover padding from a previous question first
        container.style.paddingBottom = "";
        // Add extra bottom padding so the question can scroll to the middle
        container.style.paddingBottom = `${container.clientHeight * 0.5}px`;
        // Scroll to the very bottom — the padding creates space so the question sits in the middle
        container.scrollTop = container.scrollHeight;
      } else if (waitingForResponseRef.current) {
        // Response arrived — keep padding, don't scroll
        // Padding will be removed by onTypewriterComplete callback after animation finishes
      } else {
        // Normal case (loading chat history, greeting, etc.) — scroll to bottom
        container.style.paddingBottom = "";
        container.scrollTop = container.scrollHeight;
      }
      dispatch(getChatLikesDislikeStatus());
    }
  }, [messages]);

  // chatbot response/converstaion
  useEffect(() => {
    if (chatFlag && qnaDataStaus === 200) {
      setTimeout(() => {
        setQnastore(qnaData);
        setchatFlag(false);

        // Show the response immediately without waiting for chat history save
        setMessages((prevMessages) => [
          ...prevMessages.slice(0, -1),
          {
            isQuestion: false,
            text: qnaData.botResponse || "Unable to provide an answer at this time, please try again.",
            sourceData: qnaData.source,
            isRegenerate: qnaData.isRegenerated,
            showImagesOption: qnaData.chatMeta?.showImagesOption || "fullPages",
            classification: qnaData.chatMeta?.classification || "",
            imageBlobPaths:
              qnaData.chatMeta && qnaData.chatMeta.imageBlobPaths
                ? qnaData.chatMeta.imageBlobPaths
                : "",
            imageBlobURLs:
              qnaData.chatMeta && qnaData.chatMeta.imageBlobURLs
                ? qnaData.chatMeta.imageBlobURLs
                : "",
            // These relative "images/<path>" entries came from a live qna-service
            // answer, so authenticated fetches must be prefixed with its API path.
            imageApiEndpoint: process.env.REACT_APP_QUERY_API_ENDPOINT,
            chatConversationId: "",
            userQuestion: QuestionText,
            threadId:
              qnaData.chatMeta && qnaData.chatMeta.threadId
                ? qnaData.chatMeta.threadId
                : "",
            quickResponses: qnaData.chatMeta?.quickResponses || [],
          },
        ]);

        // qna-service detected a live agent request: open the same modal as "Agent-Help"
        if (qnaData.chatMeta?.liveAgentRequest) {
          handleShowAgentHelpModal();
        }

        // Save chat history in the background
        createChatHistory(qnaData);

        // Track token usage after successful QnA response
        trackTokenUsage(qnaData);

        dispatch(QnaChatbotSuccess([]));
      }, 500);
    } else if (chatFlag && qnaDataStaus && qnaDataStaus !== 200) {
      // API returned an error status — replace "Generating Response..." with fallback message
      setchatFlag(false);
      setMessages((prevMessages) => {
        const updated = [...prevMessages];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (!updated[i].isQuestion && updated[i].text === "Generating Response...") {
            updated[i] = {
              isQuestion: false,
              text: "Unable to provide an answer at this time, please try again.",
            };
            break;
          }
        }
        return updated;
      });
      dispatch(QnaChatbotSuccess([]));
    }
  }, [
    qnaData,
    chatFlag,
    qnaDataStaus,
    successConversationData,
    chatHistoryIds,
  ]);

  // response for new chat/register chat
  useEffect(() => {
    if (suggestionData && suggestionStatus === 200) {
      setSuggestionOptions(suggestionData);
    } else {
      dispatch(getDefaultFeedbackSuggestion());
    }
  }, [suggestionData, suggestionStatus]);

  const updateDocumentSummarizationData = (data) => {
    // Check if the fileName already exists in the array
    const existingDataIndex = documentSummarizationDataArray.findIndex(
      (item) => item.fileName === data.fileName
    );

    if (existingDataIndex !== -1) {
      // If the fileName exists, update the summary
      const updatedDataArray = [...documentSummarizationDataArray];
      updatedDataArray[existingDataIndex].summary = data.summary;
      setDocumentSummarizationDataArray(updatedDataArray);
    } else {
      // If the fileName doesn't exist, add the data to the array
      setDocumentSummarizationDataArray((prevArray) => [...prevArray, data]);
    }
  };

  // fetch chat history
  const fetchData = (chatBotQueryParam) => {
    try {
      dispatch(getChatHistoryConversation(chatBotQueryParam));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };


  //save history conversation
  const createChatHistory = async (chatData) => {
    const payload = {
      userQuestion: chatData.userQuestion,
      botResponse: chatData.botResponse,
      source: chatData.source,
      isRegenerated: chatData.isRegenerated,
      showImagesOption: chatData.chatMeta.showImagesOption,
      docSettingsVersion: String(DocSettingsvalue.version),
      qnaSettingsVersion: String(promptvalue.version),
      qnaSettingsId: promptvalue._id,
      docSettingsId: DocSettingsvalue._id,
      imageBlobPaths: chatData.chatMeta.imageBlobPaths,
      imageBlobURLs: chatData.chatMeta.imageBlobURLs,
      threadId: threadId, // Include threadId in the payload
    };
    try {
      await dispatch(SaveHistoryConversation(payload, chatData.chatMetadataId));
    } catch (e) {
      console.error("Save conversation failed", e);
    }
  };

  // Track token usage
  const trackTokenUsage = async (qnaResponseData) => {
    try {
      // Check if token usage data exists in the response (handle both camelCase and snake_case)
      const tokenUsage = qnaResponseData?.tokenUsage || qnaResponseData?.token_usage;
      if (!tokenUsage) {
        console.log('No token usage data found in QnA response');
        return;
      }

      // Simple cost estimation based on model and token counts
      const calculateEstimatedCost = (model, promptTokens, completionTokens) => {
        // OpenAI pricing estimates (per 1K tokens)
        const pricingTable = {
          'gpt-3.5-turbo': { prompt: 0.0015, completion: 0.002 },
          'gpt-4': { prompt: 0.03, completion: 0.06 },
          'gpt-4o-mini': { prompt: 0.00015, completion: 0.0006 },
          'gpt-4o': { prompt: 0.0025, completion: 0.01 },
          'gpt-4.1-mini': { prompt: 0.0003, completion: 0.0012 },
          'gpt-4.1': { prompt: 0.002, completion: 0.008 },
          'gpt-5.1': { prompt: 0.003, completion: 0.012 },
          'gpt-5.2': { prompt: 0.003, completion: 0.012 },
          'gpt-5.4-mini': { prompt: 0.0011, completion: 0.0044 },
          'gpt-5.4': { prompt: 0.005, completion: 0.015 },
          'gpt-6-luna': { prompt: 0.0001, completion: 0 },
          'default': { prompt: 0.001, completion: 0.002 }
        };

        const pricing = pricingTable[model] || pricingTable['default'];
        const promptCost = (promptTokens / 1000) * pricing.prompt;
        const completionCost = (completionTokens / 1000) * pricing.completion;

        return promptCost + completionCost;
      };

      const modelName = tokenUsage.model || 'unknown';
      const promptTokens = tokenUsage.prompt_tokens || 0;
      const completionTokens = tokenUsage.completion_tokens || 0;
      const estimatedCost = calculateEstimatedCost(modelName, promptTokens, completionTokens);

      const userId = userToken?.oid || userToken?._id;

      // Extract essential token usage data for dashboard
      const tokenUsagePayload = {
        chatId: chatBotQueryParam || newQueryParam,
        datasetId: chatBotDatasetParam,
        userId: userId || 'unknown',
        userName: userName || 'Unknown User',
        promptTokens: promptTokens,
        completionTokens: completionTokens,
        totalTokens: tokenUsage.total_tokens || 0,
        model: modelName,
        estimatedCost: estimatedCost,
        isRegenerated: qnaResponseData.isRegenerated || false,
        sourceCount: qnaResponseData.source?.length || 0,
        questionLength: qnaResponseData.userQuestion?.length || 0,
        responseLength: qnaResponseData.botResponse?.length || 0,
        // Usage settings from organization
        maxQuestionsPerSession: orgDetails?.maxQuestionsPerSession || 30,
        costPerSession: orgDetails?.costPerSession || 3.0,
        maxTokenUsagePerUserPerMonth: orgDetails?.maxTokenUsagePerUserPerMonth || 100000
      };

      console.log('Tracking token usage:', tokenUsagePayload);

      // Call the token usage API
      const response = await dispatch(saveTokenUsageAction(tokenUsagePayload));

      if (response?.data?.success) {
        console.log(`Token usage tracked successfully. Question #${response.data.questionNumber}, Session #${response.data.sessionNumber}`);
      } else {
        console.warn('Token usage tracking failed:', response?.data?.error || 'Unknown error');
      }

    } catch (error) {
      console.error('Error tracking token usage:', error);
      // Don't throw error to avoid breaking chat flow
    }
  };

  // Build chat history for Agent-Help payloads (prefers persisted chatData)
  const buildAgentHistory = () => {
    if (Array.isArray(chatData) && chatData.length) {
      const qs = [];
      const ans = [];
      chatData.forEach((c) => {
        if (typeof c.userQuestion === "string") qs.push(c.userQuestion);
        if (typeof c.botResponse === "string") ans.push(c.botResponse);
      });
      return { qs, ans };
    }

    // Fall back to in-memory messages
    const qs = [];
    const ans = [];
    messages.forEach((m) => {
      if (m.isInitialGreeting) return;
      if (m.isQuestion) {
        qs.push(m.text || "");
      } else {
        if (m.userQuestion) qs.push(m.userQuestion);
        ans.push(m.text || "");
      }
    });
    return { qs, ans };
  };

  // Show modal to collect email and company name before requesting agent
  const handleShowAgentHelpModal = () => {
    // A request is already pending (isAgentHelp) or the agent has joined (agentHelp):
    // do not open the form again, which would create a second request.
    if (isAgentHelp || agentHelp) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          isQuestion: false,
          text: "A live agent request is already in progress. A team member will join you shortly.",
          isAgentMessage: true,
        },
      ]);
      return;
    }
    if (!((DocSettingsvalue?.serviceAgentEnabled && successAgentEmail) || currentUserType === 'ExternalUser')) {
      handleAgentAnswer("Please Configure Agent Email to connect to Live Chat Support.");
      showToaster("Agent Email not configured.", "error");
      return;
    }
    setAgentHelpUserEmail("");
    setAgentHelpCompanyName("");
    setAgentHelpModalError("");
    setShowAgentHelpModal(true);
  };

  // Close agent help modal
  const handleAgentHelpModalClose = () => {
    setShowAgentHelpModal(false);
    setAgentHelpUserEmail("");
    setAgentHelpCompanyName("");
    setAgentHelpModalError("");
  };

  // Process agent help request after collecting email and company name
  const handleAgentHelpSubmit = async () => {
    // Clear previous errors
    setAgentHelpModalError("");
    // Validate email
    if (!agentHelpUserEmail || !agentHelpUserEmail.trim()) {
      setAgentHelpModalError("Please enter your email address.");
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(agentHelpUserEmail.trim())) {
      setAgentHelpModalError("Please enter a valid email address.");
      return;
    }
    // Validate company name
    if (!agentHelpCompanyName || !agentHelpCompanyName.trim()) {
      setAgentHelpModalError("Please enter your company name.");
      return;
    }

    try {
      setShowAgentHelpModal(false);
      // Add "Live Agent Help requested." message to the chat UI after successful submission
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: true, text: "Live Agent Help requested." },
      ]);
      // Show "Connecting..." message after a brief delay
      setTimeout(() => {
        setMessages((prevMessages) => [
          ...prevMessages,
          { isQuestion: false, text: 'Connecting you to Live Chat Support now...Please wait.', isAgentMessage: true },
        ]);
      }, 2200);
      const { qs, ans } = buildAgentHistory();
      showToaster("Live Agent Help request sent", "success");
      // Clear the previous live-agent session status so a stale value can't leak into the new request
      dispatch(resetAgentStatus());
      setIsAgentHelp(true);
      setAgentRequestFailed(false);
      setAgentLeft(false);
      const payload = {
        datasetId: chatBotDatasetParam,
        datasetTitle: datasetParamttile,
        chatId: chatBotQueryParam || newQueryParam,
        userName: userName,
        chatHistoryQuestions: qs,
        chatHistoryAnswers: ans,
        userEmail: agentHelpUserEmail.trim(),
        companyName: agentHelpCompanyName.trim(),
      };
      const reg = await dispatch(humanAgentRegister(payload));
      const agentId = reg?.agentId || reg?.data?.agentId || reg?.id;
      if (agentId) {
        setAgentId(agentId);
        // Update human-in-loop refs in token usage service for session dashboard
        const chatId = chatBotQueryParam || newQueryParam;
        if (chatId) {
          updateHumanInLoopRefs(chatId, agentId)
            .then((res) => {
              if (res?.data?.success) {
                console.log('Human-in-loop ref added:', agentId);
              }
            })
            .catch((err) => console.error('Failed to update human-in-loop refs:', err));
        }
        await handleUserQuestion('Agent-Help', agentId);
      } else {
        // Fallback: proceed with existing state-based flow
        await handleUserQuestion('Agent-Help');
      }
      // Clear the form
      setAgentHelpUserEmail("");
      setAgentHelpCompanyName("");
    } catch (e) {
      console.error('Agent registration failed', e);
      showToaster("Failed to initiate Live Agent Help.", "error");
    }
  };

  // Keep quitAgentFnRef current every render so parent can trigger quit
  if (quitAgentFnRef) {
    quitAgentFnRef.current = () => handleSendQuestion('Quit Agent');
  }

  //  Register chatbot session
  const handleSendQuestion = async (questionText) => {
    if (questionText.includes("Agent-Help")) {
      if (
        questionText.includes("Agent-Help") &&
        ((DocSettingsvalue?.serviceAgentEnabled && successAgentEmail) || currentUserType === 'ExternalUser')
      ) {
        // Show modal to collect email and company name
        handleShowAgentHelpModal();
        return;
      }
      else if (questionText.includes("Agent-Help")) {
        handleAgentAnswer("Please Configure Agent Email to connect to Live Chat Support.");
        showToaster("Agent Email not configured.", "error");
      }
    } else if (isAgentScreen && agentValid !== false) {
      handleAgentAnswer(questionText);
    }
    else if (agentHelp && !isAgentScreen) {
      handleUserQuestion(questionText);
    }
    else {
      try {
        setQuestionText(questionText);
        // Reset threadId when starting a new chat
        setThreadId("");

        // If it's the first time a question is asked, ensure the greeting message stays
        setMessages((prevMessages) => {
          const hasGreeting = prevMessages.some(
            (msg) => msg.isInitialGreeting === true
          );

          const updatedMessages = [
            ...prevMessages,
            { isQuestion: true, text: questionText },
            {
              isQuestion: false,
              text: "Generating Response...",
              imageBlobPaths: [],
              imageBlobURLs: [],
            },
          ].filter(Boolean);

          // Ensure the greeting stays as the first message if it exists
          return hasGreeting
            ? updatedMessages
            : [
              {
                isQuestion: false,
                text: isQuickChatMode
                  ? `Hey ${userName}, I can help you answer any questions, summarize meeting notes, write content, solve problems, and much more. What would you like to explore today?`
                  : `Hey ${userName}, how may I help you today?`,
                sourceData: [],
                imageBlobPaths: [],
                imageBlobURLs: [],
                chatConversationId: "",
                userQuestion: "",
                threadId: "",
                isInitialGreeting: true,
              },
              ...updatedMessages,
            ];
        });

        if (newQueryParam === null && !chatBotQueryParam) {
          const requestData = {
            datasetId: chatBotDatasetParam,
            title: questionText,
            datasetName: datasetParamttile,
            systemPrompt: changeParams.prompt,
            kValue: changeParams.kValue,
            maxOutputToken: changeParams.maxTokens,
            thresholdValue: changeParams.thresholdValue,
            reRanker: changeParams.isRerankerEnabled,
            openAITemperature: changeParams.temperature,
            modelName: changeParams.modelName,
            questionGenPrompt: changeParams.questionGeneratorPrompt,
            clarifyPrompt: changeParams.clarifyPrompt,
            topPValue: changeParams.topPValue,
            retrievalStrategy: changeParams.retrievalStrategy,
            responseFormat: changeParams.responseFormat,
            showImagesOption: changeParams.showImagesOption,
            tableSchema: changeParams.tableSchema,
          };

          dispatch(createChatHistoryData(requestData));
        } else {
          if (chatBotQueryParam) {
            handleSendQuestionToChatbot(questionText, chatBotQueryParam, false);
          } else {
            handleSendQuestionToChatbot(questionText, newQueryParam, false);
          }
        }

        setTimeout(() => {
          setchatFlag(true);
        }, 500);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };
  const handleFetchCombineData = (userId, chatMetaId) => {
    try {
      // console.log("Calling fetchCombinedData with userId:", userId, "and chatMetaId:", chatMetaId);
      dispatch(fetchCombinedData(userId, chatMetaId));
    } catch (error) {
      console.error("Error calling fetchcombinedata:", error);
    }
  };
  // send question to chatbot
  const handleSendQuestionToChatbot = (
    questionText,
    chatID,
    isRegenerate = false,
    userFeedback = " ",
    quickResponses = "",
  ) => {
    try {
      dispatch(getAgentEmail(chatBotDatasetParam));
      const questionsVal = [];
      const answersVal = [];
      if (messages.length > 0) {
        messages.forEach((item) => {
          if (item.isQuestion) {
            questionsVal.push(item.text || "");
          } else {
            answersVal.push(item.text || "");
          }
        });
      }
      const requestData = {
        question: questionText,
        threadId: threadId, // Use the threadId from state
        isRegenerate: isRegenerate,
        kValue: isAssistantDataset
          ? assistantParams?.kValue ?? process.env.REACT_APP_DEFAULT_K_VALUE
          : changeParams.kValue,
        maxOutputToken: changeParams.maxTokens,
        thresholdValue: changeParams.thresholdValue,
        reRanker: changeParams.isRerankerEnabled,
        openAITemperature: changeParams.temperature,
        modelName: changeParams.modelName,
        questionGenPrompt: changeParams.questionGeneratorPrompt,
        clarifyPrompt:
          changeParams?.clarifyPrompt ??
          promptvalue?.clarifyPrompt ??
          process.env.REACT_APP_DEFAULT_CLARIFY_PROMPT,
        isClarifyPromptEnabled:
          changeParams?.isClarifyPromptEnabled ??
          promptvalue?.isClarifyPromptEnabled ??
          false, // defaulting to false if not set
        topPValue: changeParams.topPValue,
        retrievalStrategy: changeParams.retrievalStrategy,
        responseFormat: changeParams.responseFormat,
        regeneratePrompt: changeParams.regeneratePrompt,
        regenerateWithFeedbackPrompt: changeParams.regenerateWithFeedbackPrompt,
        showImagesOption: changeParams.showImagesOption,
        tableSchema: changeParams.tableSchema,
        userFeedback: userFeedback,
        history: {
          questions: questionsVal.length > 0 ? questionsVal.slice(-3) : [],
          answers: answersVal.length > 0 ? answersVal.slice(-3) : [],
        },
        embeddingModel:
          DocSettingsvalue?.embeddingModel ??
          process.env.REACT_APP_EMBEDDING_MODEL,
        serviceAgentEnabled: isAssistantDataset
          ? assistantParams?.serviceAgentEnabled ??
          false
          : changeParams?.serviceAgentEnabled ??
          DocSettingsvalue?.serviceAgentEnabled ??
          false,
        // serviceAgentEnabled: DocSettingsvalue?.serviceAgentEnabled ?? false,
        systemPrompt: isAssistantDataset
          ? assistantParameters?.systemInstructions ?? assistantParametersValue?.systemInstructions ?? process.env.REACT_APP_DEFAULT_PROMPT
          : changeParams?.prompt ?? promptvalue?.prompt ?? process.env.REACT_APP_DEFAULT_PROMPT,
        kValue: isAssistantDataset
          ? assistantParams?.kValue ??
          assistantParametersValue?.kValue ??
          process.env.REACT_APP_DEFAULT_K_VALUE
          :
          changeParams?.kValue ??
          promptvalue?.kValue ??
          process.env.REACT_APP_DEFAULT_K_VALUE,
        maxOutputToken:
          changeParams?.maxTokens ??
          promptvalue?.maxTokens ??
          process.env.REACT_APP_DEFAULT_MAX_TOKENS,
        thresholdValue:
          changeParams?.thresholdValue ??
          promptvalue?.thresholdValue ??
          process.env.REACT_APP_DEFAULT_THRESHOLD_VALUE,
        reRanker:
          changeParams?.isRerankerEnabled ??
          promptvalue?.isRerankerEnabled ??
          process.env.REACT_APP_DEFAULT_RERANKER,
        openAITemperature: isAssistantDataset
          ? assistantParams?.temperature ??
          assistantParams?.temperature ??
          process.env.REACT_APP_DEFAULT_TEMPERATURE
          :
          changeParams?.temperature ??
          promptvalue?.temperature ??
          process.env.REACT_APP_DEFAULT_TEMPERATURE,
        modelName: isAssistantDataset
          ? assistantParams?.openAIModel ??
          assistantParams?.openAIModel ??
          process.env.REACT_APP_DEFAULT_MODEL_NAME
          :
          changeParams?.modelName ??
          promptvalue?.modelName ??
          process.env.REACT_APP_DEFAULT_MODEL_NAME,
        questionGenPrompt:
          changeParams?.questionGeneratorPrompt ??
          promptvalue?.questionGeneratorPrompt ??
          process.env.REACT_APP_DEFAULT_QUESTION_GEN_PROMPT,
        clarifyPrompt:
          changeParams?.clarifyPrompt ??
          promptvalue?.clarifyPrompt ??
          process.env.REACT_APP_DEFAULT_CLARIFY_PROMPT,
        topPValue: isAssistantDataset
          ? assistantParams?.topPValue ??
          assistantParams?.topP ??
          process.env.REACT_APP_DEFAULT_TOP_P_VALUE
          :
          changeParams?.topPValue ??
          promptvalue?.topPValue ??
          process.env.REACT_APP_DEFAULT_TOP_P_VALUE,
        regeneratePrompt:
          changeParams?.regeneratePrompt ??
          promptvalue?.regeneratePrompt ??
          process.env.REACT_APP_DEFAULT_REGENERATE_PROMPT,
        regenerateWithFeedbackPrompt:
          changeParams?.regenerateWithFeedbackPrompt ??
          promptvalue?.regenerateWithFeedbackPrompt ??
          process.env.REACT_APP_DEFAULT_REGENERATE_WITH_FEEDBACK_PROMPT,
        showImagesOption: isAssistantDataset
          ? assistantParams?.showImagesOption ??
          assistantParams?.showImagesOption ??
          process.env.REACT_APP_DEFAULT_SHOW_IMAGES_OPTION
          :
          changeParams?.showImagesOption ??
          promptvalue?.showImagesOption ??
          process.env.REACT_APP_DEFAULT_SHOW_IMAGES_OPTION,
        showSource:
          changeParams?.showSource ??
          promptvalue?.showSource ??
          (process.env.REACT_APP_DEFAULT_SHOW_SOURCE !== 'false'),
        tableSchema:
          changeParams?.tableSchema ??
          promptvalue?.tableSchema ??
          process.env.REACT_APP_DEFAULT_TABLE_SCHEMA,
        quickResponses: quickResponses,
        // Lets qna-service know a live agent request is already pending
        liveAgentRequested: !!(isAgentHelp || agentHelp),
        codeInterpreterEnabled:
          assistantParams?.codeInterpreterEnabled ??
          assistantParams?.codeInterpreterEnabled ??
          false,
        agenticQnAEnabled:
          changeParams?.agenticQnAEnabled === true ||
          changeParams?.agenticQnAEnabled === "true" ||
          promptvalue?.agenticQnAEnabled === true ||
          promptvalue?.agenticQnAEnabled === "true",
        agentPrompt:
          changeParams?.agentPrompt ??
          promptvalue?.agentPrompt ??
          '',
        // MCP Tools configuration for dataset-specific tools
        mcpTools:
          changeParams?.mcpTools ??
          promptvalue?.mcpTools ??
          [],
        selectedMcpTools:
          changeParams?.selectedMcpTools ??
          promptvalue?.selectedMcpTools ??
          [],
      };
      if (messages.length > 0 && messages[1]?.threadId) {
        requestData.threadId = messages[1].threadId; // Add your key-value pair here
      }

      // Always add ref_datasetid when in QuickChat mode
      if (isQuickChatMode) {
        // Find the reference dataset (the one with isReferenceDataset = true) using allDatasets
        const referenceDataset = allDatasets?.find(dataset => dataset.isReferenceDataset === true);
        requestData.ref_datasetid = referenceDataset?.id || null;
        console.log(`QuickChat detected. Reference dataset ID: ${requestData.ref_datasetid}`);
      }

      const userId = userToken?.oid || userToken?._id || userToken?.userId || "";
      const chatMetaId = chatID;
      handleFetchCombineData({ userId, chatMetaId });
      dispatch(qnaChatbotSession(requestData, chatBotDatasetParam, chatID));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleFeedbackOptionClick = (questionText, chatID, userFeedback) => {
    console.log("userFeedback is this:", userFeedback);
    setSendFeed(userFeedback);
    handleSendQuestionToChatbot(questionText, chatID, true, userFeedback);
    setTimeout(() => {
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: true, text: "" },
        {
          isQuestion: false,
          text: "Generating Response...",
          imageBlobPaths: [],
          imageBlobURLs: [],
        },
      ]);
      setchatFlag(true);
    }, 500);
  };

  const handleQuickResponseClick = (quickResponse, chatID) => {
    console.log("Quick response is this:", quickResponse);

    // Send the quick response as the new question
    handleSendQuestionToChatbot(quickResponse, chatID, true);

    // Update the messages state
    setMessages((prevMessages) => [
      ...prevMessages,
      { isQuestion: true, text: quickResponse }, // Use quickResponse as the new question
      {
        isQuestion: false,
        text: "Generating Response...",
        imageBlobPaths: [],
        imageBlobURLs: [],
      },
    ]);

    setTimeout(() => {
      setchatFlag(true);
    }, 500);
  };


  const handleRegenerate = (questionText, chatID) => {
    handleSendQuestionToChatbot(questionText, chatID, true);
    setTimeout(() => {
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: true, text: "" },
        {
          isQuestion: false,
          text: "Generating Response...",
          imageBlobPaths: [],
          imageBlobURLs: [],
        },
      ]);
      setchatFlag(true);
    }, 500);
  };

  useEffect(() => {
    const findThreadId = (data) => {
      if (data && data.length > 0) {
        const messageWithThreadId = data.find((item) => item.threadId);
        if (messageWithThreadId) {
          setThreadId(messageWithThreadId.threadId);
        }
      }
    };

    findThreadId(messages);
    findThreadId(chatData);
  }, [messages, chatData]);

  // User Agent Flow

  useEffect(() => {
    if (agentData && isAgentHelp) {
      const agentId = agentData.agentId;
      setAgentId(agentId);
      if (AgentId) {
        startAgentPolling(AgentId);
      }
    }
  }, [agentData, AgentId]);

  const hasValidatedAgent = useRef(false);
  useEffect(() => {
    if (agentIdFromUrl && !hasValidatedAgent.current) {
      checkAgentJoined(agentIdFromUrl);
      hasValidatedAgent.current = true;
    }
  }, [agentIdFromUrl]);

  const checkAgentJoined = async (agentId) => {
    try {
      dispatch(humanAgentValidate(agentId));
      dispatch(humanAgentGetStatus(agentId)); // Also fetch status to determine if closed vs active
      setValidatedAgentStatus(agentValid);
    } catch (error) {
      console.error("Error validating agent:", error);
    }
  };

  useEffect(() => {
    if (agentValid === false && isAgentScreen) {
      if (QuestionText !== 'Quit Agent' && !agentSessionTimedOut && agentStatus !== 'Closed') {
        showToaster("Someone has already joined the chat.", "error");
        setMessages([]);
        const queryParams = new URLSearchParams(location.search);
        queryParams.delete("agentId");
        queryParams.delete("cid");
        queryParams.delete("c");
        queryParams.delete("title");
        const newUrl = `${location.pathname}?${queryParams.toString()}`;
        trigger(newUrl);
      } else if (agentSessionTimedOut || agentStatus === 'Closed') {
        const message = agentStatus === 'Closed' && !agentSessionTimedOut
          ? "User has ended the session."
          : "Agent session duration timeout";
        showToaster(message, "error");
        setAgentSessionTimedOut(false); // Reset for future sessions
        // Preserve conversation history on the agent screen so the agent can still
        // review the chat after the session ends. The end-of-session notice message
        // is appended by the question/status handlers above. Do not auto-navigate
        // away — the agent can leave the screen on their own.
      }
    }
    else if (agentValid === true && isAgentScreen) {
      // Check if session already closed (user timed out before agent joined,
      // or the agent ended the session themselves before validate returned).
      if (agentStatus === 'Closed') {
        showToaster("Agent session duration timeout", "error");
        // Keep messages intact so the agent can still review the conversation.
      } else if (agentStatus) {
        // Only enable if status is not Closed and status is available
        if (userRole === 'User') {
          setMessages([]);
        }
        showToaster(`Live Agent Help enabled`, "success");
      }
    }
  }, [agentValid, agentStatus]);

  useEffect(() => {
    if (agentStatus == 'Active') {
      setAgentJoined(true);
    }
  }, [agentStatus]);

  useEffect(() => {
    if (agentStatus === 'Active' && isAgentHelp && AgentId !== null && !agentLeft) {
      showToaster("Agent Joined the chat", "success");
      if (agentPollingInterval) {
        setAgentHelp(true);
      }
      if (!isAgentScreen) {
        notifyAgentEvent("agent");
        setMessages((prevMessages) => [
          ...prevMessages,
          { isQuestion: false, text: "Support agent joined the chat.", isAgentMessage: true },
        ]);
        setTimeout(() => {
          setMessages((prevMessages) => [
            ...prevMessages,
            { isQuestion: false, text: "Hey! I'm here to help. I'm just catching up on the conversation so far to understand the issue. Give me just a moment and I'll be ready to help!", isAgentMessage: true },
          ]);
        }, 2000);
      }
    } else if (agentStatus === "Open" && isAgentHelp) {
      // Show "Notified" message after a brief delay
      setTimeout(() => {
        setMessages((prevMessages) => [
          ...prevMessages,
          { isQuestion: false, text: "We have notified the Support team!\nSomeone will be with you shortly.", isAgentMessage: true },
        ]);
      }, 1500);
      const timer = setTimeout(() => {
        // Set status to Closed first
        const payload = { status: 'Closed' };
        dispatch(humanAgentUpdateStatus(AgentId, payload));
        clearInterval(agentPollingInterval);
        setAgentPollingInterval(null);
        handleAgentLeft();
        setIsAgentHelp(false);
        setAgentHelp(false);
        setAgentId(null);
        setAgentLeft(true);
        // Dispatch validation to stop polling on agent screen
        if (agentIdFromUrl) {
          dispatch(humanAgentValidate(agentIdFromUrl));
        }
      }, 600000); // 10 minutes

      return () => clearTimeout(timer);
    }
    else if (agentStatus === 'Closed' && !isAgentHelp && agentLeft) {
      clearInterval(agentPollingInterval);
      setAgentPollingInterval(null);
    }
    else if (agentStatus === 'Closed' && agentLeft) {
      setIsAgentHelp(false);
      setAgentHelp(false);
      handleAgentLeft();
    }
  }, [agentLeft, agentStatus, isAgentHelp]);

  useEffect(() => {
    let responseTimer;


    if (agentHelp) {
      responseTimer = setTimeout(() => {
        const payload = {
          status: 'Closed'
        }
        dispatch(humanAgentUpdateStatus(AgentId, payload));
        setAgentLeft(true);
        // Dispatch validation to stop polling on agent screen
        if (agentIdFromUrl || AgentId) {
          dispatch(humanAgentValidate(agentIdFromUrl || AgentId));
        }
      }, 600000); // 10 minutes
    }

    // Clear the timeout when the component unmounts or dependencies change
    return () => clearTimeout(responseTimer);
  }, [answer, agentHelp]);

  const handleAgentLeft = () => {
    clearInterval(agentPollingInterval);
    setAgentPollingInterval(null);
    setAgentId(null);
    setAgentHelp(false);
    setAgentLeft(true);
    // Reset Redux agent states to prevent stale responses from appearing after timeout
    dispatch(resetAnswer());
    dispatch(resetAgentResponse());
    dispatch(resetQuestion());
    dispatch(resetUserQuestion());
    showToaster("Agents are busy. Support ticket will get created for you.", "error");
    setMessages((prevMessages) => {
      // Remove the loader and add the timeout message in its place
      const filtered = prevMessages.filter(msg => msg.isAgentLoading !== true);
      return [
        ...filtered,
        {
          isQuestion: false,
          text: "The support agents are busy. A support ticket has been created, and an agent will reach out to you shortly.",
          imageBlobPaths: [],
          imageBlobURLs: [],
        },
      ];
    });
  };

  //Timer to Handle User Question on agent screen
  useEffect(() => {
    if (isAgentScreen && agentValid !== false) {
      const interval = setInterval(async () => {
        try {
          await dispatch(getHumanAgentQnaChat(agentIdFromUrl, 'question'));
        } catch (error) {
          console.error("Error fetching user questions:", error);
        }
      }, 1000); // Poll every 1 second

      return () => clearInterval(interval); // Cleanup interval on component unmount
    }
  }, [agentHelp, isAgentScreen, agentValid]);

  // Poll for agent status on agent screen to detect when user times out
  useEffect(() => {
    if (isAgentScreen && agentIdFromUrl && agentValid !== false) {
      const statusInterval = setInterval(async () => {
        try {
          await dispatch(humanAgentGetStatus(agentIdFromUrl));
        } catch (error) {
          console.error("Error fetching agent status:", error);
        }
      }, 6000); // Poll every 6 seconds

      return () => clearInterval(statusInterval);
    }
  }, [isAgentScreen, agentIdFromUrl, agentValid]);

  // Handle status 'Closed' on agent screen (when user times out or quits).
  // Use a ref guard so the end-of-session notice is appended only once even if
  // the status poll keeps returning 'Closed' before agentValid flips to false.
  const sessionEndedNoticeShownRef = useRef(false);
  useEffect(() => {
    if (isAgentScreen && agentStatus === 'Closed' && agentValid !== false && !sessionEndedNoticeShownRef.current) {
      sessionEndedNoticeShownRef.current = true;
      // User side has timed out or closed - stop polling on agent screen
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: false, text: "The Live Chat session has ended." },
      ]);
      // Mark as timeout case so correct toaster is shown
      setAgentSessionTimedOut(true);
      // Validate to set agentValid to false and stop polling
      dispatch(humanAgentValidate(agentIdFromUrl));
    }
  }, [isAgentScreen, agentStatus, agentValid]);

  // Notify parent when agent session becomes active/inactive
  useEffect(() => {
    if (onAgentActiveChange) {
      // Agent screen: hide quit action as soon as the session closes or times out.
      // User screen: active when agentHelp is true.
      const isActive = isAgentScreen
        ? agentValid !== false && agentStatus !== 'Closed' && !agentSessionTimedOut
        : agentHelp;
      onAgentActiveChange(isActive);
    }
  }, [agentHelp, isAgentScreen, agentValid, agentStatus, agentSessionTimedOut]);

  // Tell the parent the live agent request status for the navbar badge (user screen only)
  useEffect(() => {
    if (!onAgentRequestStatusChange) return;
    let status = null;
    if (!isAgentScreen) {
      if (agentHelp) status = "joined";
      else if (isAgentHelp) status = "in_progress";
      else if (agentRequestFailed) status = "failed";
    }
    onAgentRequestStatusChange(status);
  }, [agentHelp, isAgentHelp, agentRequestFailed, isAgentScreen]);

  // Clear the navbar badge when the chat screen goes away
  useEffect(() => {
    return () => {
      if (onAgentRequestStatusChange) onAgentRequestStatusChange(null);
    };
  }, []);

  // Clear title-flash + favicon-dot once the session actually ends.
  useEffect(() => {
    if (agentLeft || agentSessionTimedOut || agentStatus === 'Closed') {
      clearAgentNotifications();
    }
  }, [agentLeft, agentSessionTimedOut, agentStatus]);

  // Safety net: clear on unmount only (do NOT depend on session vars,
  // otherwise the cleanup runs on every dep change and kills the flash).
  useEffect(() => () => clearAgentNotifications(), []);

  useEffect(() => {
    if (!isAgentScreen && agentHelp && DocSettingsvalue?.serviceAgentEnabled) {
      const interval = setInterval(async () => {
        try {
          await dispatch(getHumanAgentQnaChat(AgentId, 'answer'));
        } catch (error) {
          console.error("Error fetching agent answers:", error);
        }
      }, 1000); // Poll every 1 second

      return () => clearInterval(interval); // Cleanup interval on component unmount
    }
  }, [agentHelp]);


  useEffect(() => {
    if (agentLeft === true) {
      dispatch(humanAgentValidate(agentIdFromUrl || AgentId));
      dispatch(resetAnswer());
    }
  }, [agentLeft]);

  useEffect(() => {
    if (agentValid === false && agentLeft) {
      dispatch(resetAnswer());
    }
  }, [agentValid]);

  // User Agent communication

  useEffect(() => {
    if (type === 'question' && isAgentScreen) {
      // Dedupe: skip if we've already handled this exact question payload
      // (poll cycles return the same record until a new upsert).
      if (!questionTimestamp || lastHandledQuestionTsRef.current === questionTimestamp) {
        return;
      }
      lastHandledQuestionTsRef.current = questionTimestamp;
      if (question === 'Quit Agent') {
        setQuestionText('Quit Agent');
        setMessages((prevMessages) => {
          // Remove loader if present and add quit message
          const filtered = prevMessages.filter(msg => msg.isUserLoading !== true);
          return [
            ...filtered,
            { isQuestion: false, text: `User has ended the session.` }
          ];
        });
        showToaster('Live Agent Help disabled.', 'error');
        dispatch(humanAgentValidate(agentIdFromUrl));
      }
      else if (question === 'Agent-Help') {
        // Display "Live Agent Help requested!" instead of "Agent-Help" on agent screen
        // This is the first message so just add it (no loader to replace)
        setMessages((prevMessages) => [
          ...prevMessages,
          { isQuestion: false, text: "Live Agent Help requested." },
        ]);
      }
      else if (question) {
        // Replace the loader placeholder with actual user question
        notifyAgentEvent("user");
        setMessages((prevMessages) => {
          const loaderIndex = prevMessages.findIndex(msg => msg.isUserLoading === true);
          if (loaderIndex !== -1) {
            const updatedMessages = [...prevMessages];
            updatedMessages[loaderIndex] = { isQuestion: false, text: question, isAgentMessage: true };
            return updatedMessages;
          }
          // No loader found - this is likely the first message in conversation
          return [...prevMessages, { isQuestion: false, text: question, isAgentMessage: true }];
        });
      }
    }
    else if (question && question !== 'Agent-Help' && !isAgentScreen) {
      // Dedupe identical repeated questions on user screen
      if (!questionTimestamp || lastHandledQuestionTsRef.current === questionTimestamp) {
        return;
      }
      lastHandledQuestionTsRef.current = questionTimestamp;
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: true, text: question },
      ]);

    }
  }, [question, type, questionTimestamp]);

  useEffect(() => {
    if (!answer) return;

    // Skip adding messages if agent session has ended (agentLeft = true)
    // This prevents duplicate messages from stale polling responses after timeout
    if (agentLeft) {
      dispatch(resetAnswer());
      return;
    }

    if (type === 'answer' && isAgentScreen) {
      // On agent screen, agent's own answers are already added by handleAgentAnswer
      // Don't add them again here to avoid duplicates
      return;
    }
    // Dedupe per-field timestamp so identical answer text repeats are still rendered
    // once per send (and not multiple times per poll cycle).
    if (!answerTimestamp || lastHandledAnswerTsRef.current === answerTimestamp) {
      return;
    }
    lastHandledAnswerTsRef.current = answerTimestamp;
    {
      if (answer === 'Quit Agent') {
        clearInterval(agentPollingInterval);
        setAgentPollingInterval(null);
        setIsAgentHelp(false);
        setAgentHelp(false);
        setAgentId(null);
        setAgentLeft(true);
        setMessages((prevMessages) => {
          // Remove the loader and add the agent left message in its place
          const filtered = prevMessages.filter(msg => msg.isAgentLoading !== true);
          return [
            ...filtered,
            { isQuestion: false, text: "Agent Left the chat" },
          ];
        });
      }
      else {
        // Replace the loader placeholder with actual agent response
        notifyAgentEvent("agent");
        setMessages((prevMessages) => {
          // Find and replace the loader message
          const loaderIndex = prevMessages.findIndex(msg => msg.isAgentLoading === true);
          if (loaderIndex !== -1) {
            const updatedMessages = [...prevMessages];
            updatedMessages[loaderIndex] = { isQuestion: false, text: answer, isAgentMessage: true };
            return updatedMessages;
          }
          // Fallback: add as new message if no loader found
          return [...prevMessages, { isQuestion: false, text: answer, isAgentMessage: true }];
        });
      }
    }
  }, [answer, type, agentLeft, answerTimestamp]);

  useEffect(() => {
    if (!AgentResponse) return;

    // Skip adding messages if agent session has ended (agentLeft = true)
    // This prevents duplicate messages from stale responses after timeout
    if (agentLeft) {
      dispatch(resetAgentResponse());
      return;
    }

    // On agent screen, agent's own answers are already added by handleAgentAnswer
    // Only add system messages on agent screen, skip regular answers
    if (isAgentScreen) {
      dispatch(resetAgentResponse());
      return;
    }

    if (AgentResponse === "Connecting you to Live Chat Support now...Please wait." || AgentResponse === "We have notified the Support team!\nSomeone will be with you shortly." || AgentResponse === "Please Configure Agent Email to connect to Live Chat Support." || AgentResponse === "Support agent joined the chat." || AgentResponse === "Hey! I'm here to help. I'm just catching up on the conversation so far to understand the issue. Give me just a moment and I'll be ready to help!") {
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: false, text: AgentResponse },
      ]);
      dispatch(resetAgentResponse());
    }
    else if (AgentResponse !== 'Quit Agent') {
      //setPreviousAgentResponse(AgentResponse);
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: true, text: AgentResponse },
      ]);
    }
  }, [AgentResponse, agentLeft]);

  useEffect(() => {
    // User questions handled elsewhere:
    // - On user screen: handleUserQuestion adds the message
    // - On agent screen: [question, type] useEffect handles via polling
    // No need to add messages here - only handle cleanup
    if (UserQuestion === 'Quit Agent') {
      dispatch(resetUserQuestion());
    }
  }, [UserQuestion]);

  useEffect(() => {
    if (userRole === 'User' && agentIdFromUrl) {
      setMessages([]);
    }
  }, [userRole, agentIdFromUrl]);

  const startAgentPolling = (agentId) => {
    const interval = setInterval(async () => {
      try {
        dispatch(humanAgentGetStatus(agentId));
      } catch (error) {
        console.error("Error fetching agent status:", error);
      }
    }, 6000); // Poll every 5 seconds

    setAgentPollingInterval(interval);
    return () => clearInterval(interval); // Cleanup interval on component unmount
  };

  //handle user question for human agent
  const handleUserQuestion = async (questionText, agentIdOverride = null) => {
    if (questionText === 'Quit Agent') {
      const payload = {
        status: 'Closed'
      };
      dispatch(humanAgentUpdateStatus(AgentId, payload));
      setIsAgentHelp(false);
      setAgentHelp(false);
      setAgentId(null);
      showToaster("Live Agent Help disabled", "success");
      clearInterval(agentPollingInterval);
      setAgentPollingInterval(null);
    }
    try {
      // Add user message (no loader placeholder in agent mode)
      if (questionText !== 'Agent-Help' && questionText !== 'Quit Agent') {
        setMessages((prevMessages) => {
          const filtered = prevMessages.filter(msg => msg.isAgentLoading !== true);
          return [
            ...filtered,
            { isQuestion: true, text: questionText },
          ];
        });
      }
      const targetAgentId = agentIdOverride || AgentId;
      const payload = { question: questionText, chatId: chatBotQueryParam || newQueryParam, datasetId: chatBotDatasetParam };
      await dispatch(humanAgentQna(targetAgentId, 'question', payload));
    } catch (error) {
      console.error("Error sending question to agent:", error);
    }
  };
  //handle agent answer for human-agent
  const handleAgentAnswer = async (answerText) => {
    if (answerText === 'Quit Agent') {
      if (agentPollingInterval) {
        clearInterval(agentPollingInterval);
        setAgentPollingInterval(null);
      }
      const payload = {
        status: 'Closed'
      };
      setQuestionText('Quit Agent');
      dispatch(humanAgentUpdateStatus(agentIdFromUrl, payload))
        .then(() => {
          setIsAgentHelp(false);
          setAgentHelp(false);
          setAgentId(null);
          setAgentLeft(true);
          showToaster("Live Agent Help disabled", "success");
        })
    }
    try {
      // Add agent's answer (no loader placeholder in agent mode)
      if (answerText !== 'Quit Agent' && !['Connecting you to Live Chat Support now...Please wait.', "We have notified the Support team!\nSomeone will join you in about 2 minutes.", 'Please Configure Agent Email to connect to Live Chat Support.', 'Support agent joined the chat.', "Hey! I'm here to help. I'm just catching up on the conversation so far to understand the issue. Give me just a moment and I'll be ready to help!"].includes(answerText)) {
        setMessages((prevMessages) => {
          const filtered = prevMessages.filter(msg => msg.isUserLoading !== true);
          return [
            ...filtered,
            { isQuestion: true, text: answerText },
          ];
        });
      }
      const payload = { answer: answerText, chatId: chatBotQueryParam || newQueryParam, datasetId: chatBotDatasetParam };
      await dispatch(humanAgentQna(agentIdFromUrl, 'answer', payload));
    } catch (error) {
      console.error("Error sending answer to user:", error);
    }
  };

  useEffect(() => {
    if (!agentHelp) {
      dispatch(resetUserQuestion());
      dispatch(resetAgentResponse());
      dispatch(resetQuestion());
      dispatch(resetAnswer());
    }
  }, [agentHelp]);

  return (
    <>
      {toasterVisible && (
        <CustomToaster
          message={toasterMessage}
          duration={2800}
          onClose={closeToaster}
          position="right-top"
          appearance={toasterAppearance}
        />
      )}
      {downloadFlagLoader && <Loader />}
      {summarizationFlag ? (
        <div className="chat-message-area">
          <Summarization
            summarydata={documentSummarizationDataArray}
            listLength={doclistLength}
            title={datasetParamttile}
          />
        </div>
      ) : (
        <div className={`chat-container ${chatViewPreference === "opposite-side" ? "chat-view-opposite" : "chat-view-same"}`}>
          <div ref={messagesDivRef} className="chat-message-area">
            {summarizationFlag && (
              <Summarization summarydata={documentSummarizationDataArray} />
            )

            }

            {messages.length === 0 ? (
              <div className="message answer">
                <div className="outer-chat-bubble">
                  <div className="icons-answer icons-answer-greeting">
                    <img src="/images/question-chat-icon.svg" alt="chat Icon" />
                  </div>
                  <div className="question-box" data-cy="ai-response-cy">
                    {userName ? (
                      <p>{isQuickChatMode
                        ? `Hey ${userName}, I can help you answer any questions, summarize meeting notes, write content, solve problems, and much more. What would you like to explore today?`
                        : `Hey ${userName}, how may I help you today?`
                      }</p>
                    ) : (
                      <p>{isQuickChatMode
                        ? "Hey there, I can help you answer any questions, summarize meeting notes, write content, solve problems, and much more. What would you like to explore today?"
                        : "Hey there, how may I help you today?"
                      }</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <MessageList
                messages={messages}
                enabledownloadFlagLoader={enabledownloadFlagLoader}
                disabledownloadFlagLoader={disabledownloadFlagLoader}
                suggestionOptions={suggestionOptions}
                chatHistoryIds={chatHistoryIds}
                handleRegenerate={handleRegenerate}
                handleSendQuestion={handleSendQuestion}
                chatID={chatBotQueryParam}
                showImagesOption={
                  changeParams?.showImagesOption ??
                  promptvalue?.showImagesOption ??
                  process.env.REACT_APP_DEFAULT_SHOW_IMAGES_OPTION
                }
                handleFeedbackOptionClick={handleFeedbackOptionClick} // Pass handleFeedbackOptionClick to MessageList
                handleQuickResponseClick={handleQuickResponseClick} // Pass handleQuickResponseClick to MessageList
                sendFeed={sendFeed}
                setSendFeed={setSendFeed}
                DocSettingsvalue={DocSettingsvalue} // Pass DocSettingsvalue to MessageList
                successAgentEmail={successAgentEmail}
                showSource={
                  changeParams?.showSource ??
                  promptvalue?.showSource ??
                  true
                }
                isAgentHelp={agentHelp}
                isAgentHelpRequested={isAgentHelp}
                isAgentScreen={isAgentScreen}
                onTypewriterComplete={() => {
                  if (messagesDivRef.current && waitingForResponseRef.current) {
                    const container = messagesDivRef.current;
                    const lastMsg = messages[messages.length - 1];
                    const responseText = lastMsg && !lastMsg.isQuestion ? (lastMsg.text || "") : "";
                    const lineCount = responseText.split("\n").length;
                    waitingForResponseRef.current = false;
                    container.style.paddingBottom = "";
                    if (lineCount > 3) {
                      // Long QnA response — preserve the user's scroll position
                      // so they can read from the top of the response
                      const savedScroll = container.scrollTop;
                      container.style.overflowY = "hidden";
                      container.scrollTop = savedScroll;
                      requestAnimationFrame(() => {
                        container.scrollTop = savedScroll;
                        container.style.overflowY = "auto";
                      });
                    } else {
                      // Short response (agent comms, 1-3 lines) — scroll to bottom
                      container.scrollTop = container.scrollHeight;
                    }
                  }
                }}
              />
            )}

            {(showLoader || isHandleViewCalled) && (
              <div className="history-loading-overlay">
                <div className="history-loading-content">
                  <div className="history-loading-spinner"></div>
                  <p className="history-loading-text">
                    {historyLoadingTakingLong
                      ? "This is taking a bit longer than usual, please wait..."
                      : "Loading from history..."}
                  </p>
                </div>
              </div>
            )}
          </div>
          {!isHumanAgentHistoryView && (
          <InputArea
            onSendQuestion={handleSendQuestion}
            disableInputArea={disableInputArea || !initialParametersLoaded || (isAgentScreen && agentValid === false)}
            isQuickChatMode={isQuickChatMode}
            datasetId={chatBotDatasetParam}
            showToaster={showToaster}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            setActiveTab={setActiveTab}
            currentUserType={currentUserType}
          />
          )}
        </div>
      )}

      {/* Live Agent Help Modal - Collect Email and Company Name */}
      <Modal
        open={showAgentHelpModal}
        onClose={handleAgentHelpModalClose}
        size="tiny"
        className="add-document-model filterPopup feedbackPopup"
      >
        <Modal.Content>
          <Header as="h3">Connect to Live Agent Support</Header>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Please provide your contact information to connect with a live agent.
          </p>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Email Address <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              type="email"
              value={agentHelpUserEmail}
              onChange={(e) => {
                const val = e.target.value;
                setAgentHelpUserEmail(val);
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!val || !val.trim()) {
                  setAgentHelpModalError("Please enter your email address.");
                } else if (!emailRegex.test(val.trim())) {
                  setAgentHelpModalError("Please enter a valid email address.");
                } else {
                  setAgentHelpModalError("");
                }
              }}
              onBlur={() => {
                if (!agentHelpUserEmail || !agentHelpUserEmail.trim()) {
                  setAgentHelpModalError("Please enter your email address.");
                } else {
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(agentHelpUserEmail.trim())) {
                    setAgentHelpModalError("Please enter a valid email address.");
                  }
                }
              }}
              placeholder="Enter your email address"
              className="add-datset-input"
              data-cy="agent-help-email-input"
              fluid
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Company Name <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              type="text"
              value={agentHelpCompanyName}
              onChange={(e) => {
                setAgentHelpCompanyName(e.target.value);
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (agentHelpModalError && agentHelpModalError.toLowerCase().includes('company')) {
                  setAgentHelpModalError("");
                } else if (agentHelpModalError && emailRegex.test(agentHelpUserEmail.trim())) {
                  setAgentHelpModalError("");
                }
              }}
              placeholder="Enter your company name"
              className="add-datset-input"
              data-cy="agent-help-company-input"
              fluid
            />
          </div>
          {agentHelpModalError && (
            <div style={{
              marginTop: '15px',
              padding: '12px 16px',
              backgroundColor: '#FFF5F5',
              border: '1px solid #FFE8E8',
              borderLeft: '4px solid #FF3859',
              borderRadius: '8px',
              color: '#14181F',
              fontSize: '14px',
              lineHeight: '1.6',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF3859" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span style={{ flex: 1 }}>{agentHelpModalError}</span>
            </div>
          )}
          <p style={{ color: '#888', fontSize: '12px', fontStyle: 'italic', marginTop: '15px', marginBottom: '0', textAlign: 'center' }}>
            Your Name, Email and Company Name may be shared with Innferre when using Innferre AI Assistant.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <button
            className="cancel-btn"
            onClick={handleAgentHelpModalClose}
            data-cy="agent-help-cancel-btn"
          >
            Cancel
          </button>
          <button
            disabled={!agentHelpUserEmail.trim() || !agentHelpCompanyName.trim() || !!agentHelpModalError}
            className="add-btn"
            onClick={handleAgentHelpSubmit}
            data-cy="agent-help-submit-btn"
            style={{ width: '35%' }}
          >
            Connect to Agent
          </button>
        </Modal.Actions>
      </Modal>

      {/* Empty Conversation Modal - shown when a chat resolves with no saved messages */}
      <ConfirmActionModalComponent
        open={showEmptyChatModal}
        onClose={handleContinueEmptyChat}
        title="No Conversation Found"
        message="This chat has no messages yet. Continue chatting, or delete it if you no longer need it."
        primaryText="Continue"
        onPrimaryClick={handleContinueEmptyChat}
        secondaryText="Delete Chat"
        onSecondaryClick={handleDeleteEmptyChat}
        secondaryLoading={isDeletingEmptyChat}
        dataCy="empty-chat"
      />
    </>
  );
};

export default ChatBot;
